// sequence-detail.tsx — Surface 3 of the Drip Campaigns redesign.
//
// Ported faithfully from the handoff's `prototype/src/drip-detail.jsx`:
// timeline-primary, reply-exit funnel as a quiet sidebar stat. The prototype's
// inline-style approach is kept verbatim for pixel fidelity (it renders inside
// a `.campaigns-rd` wrapper that supplies the design tokens).
//
// All numbers are REAL — derived from the live Drip doc, its per-step campaigns
// (tagged `dripId`/`dripStepIndex`), per-campaign message-log stats, and the
// drip's monotonic `exitedRecipients` set. Where a stat genuinely isn't
// available yet (e.g. a step whose campaign hasn't launched), we show 0 rather
// than inventing a number — see the per-derivation notes inline.

import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import React, { useEffect, useMemo, useState } from 'react';
import { add, formatDistanceToNow, type Duration } from 'date-fns';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { TFunction } from 'i18next';
import {
  ChevronLeft,
  Pencil,
  Clock,
  Reply,
  Check,
  Send,
  CornerDownRight,
  ListFilter,
  ChevronDown,
  Users,
  Hourglass,
  UserMinus,
  X,
  Image as ImageIcon,
  Video,
  Mic,
  FileText,
  Lock,
  SkipForward,
  Undo2,
  MessageSquare,
  CalendarClock,
} from 'lucide-react';

import { namespace } from '@common/config/constants';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import {
  dripsSelectOneObjectByQuery,
  DBDripThunks,
  campaignsSelectByQuery,
  campaignsSelectOneObjectByQuery,
  DBCampaignThunks,
  DBMessageLogThunks,
  DBBoardsThunks,
  DBTasksThunks,
  tasksSelectByQuery,
  DBTaskColumnThunks,
  taskColumnSelectByQuery,
} from '@common-reducers/DBServiceThunks';
import { getWaContactBySerializedId } from '@common-reducers/wa-contact-entity-lookup';
import { calculatedWaContactName } from '@common-reducers/WhatsAppSelectors';
import ContactAvatar from '@extension/context/content/components/bulk-dashboard/contact-table/contact-avatar';
import { DestinationWithList } from '@common/components/destination-avatars/destination-avatars';
import { useAudienceMemberHints, useCampaignDestination } from '@common/components/destination-avatars/use-campaign-destination';
import {
  getTaskSumByBoardIdRecord,
  selectCampaignProgressStats,
} from '@common-reducers/CampaignSelectors';
import { computeCampaignProgress, type CampaignProgressStats } from '@common/services/campaign-progress';
import { CAMPAIGN_STATUS_COLORS } from '@common/services/campaign-status-colors';
import { fetchCampaignStatsThunk } from '@common-reducers/CampaignThunks';

import type { Drip, DripStatus, DripStep, DripStepGap } from '@bt-common/schemas/bt/drip';
import type { Template as StepTemplate } from '@bt-common/schemas/bt/campaign';
import type { Campaign, CampaignStatus } from '@common-models/campaign-model';
import type { Template as CampaignTemplate } from '@common-models/template';

import { StatusLabel, AssetBadge, Eyebrow, DelayControl, Toggle, templateToPreview, gapText, type DelayGap } from './primitives';
import { canEditStopOnReply, currentStopOnReply, stopOnReplyPatch } from './stop-on-reply-edit';
import { CampaignMessageBubble, templateToPreviewMessage } from './campaign-message-bubble';
import { deriveSentContent, templateUsesMergeFields, stepPreviewMode, type SentContentState } from './sent-content';
import { DripStepEditModal } from './drip-step-edit-modal';
import { SaveAsDripTemplateModal } from './save-as-drip-template-modal';
import { applyGapAfterStep, projectStepSendDates } from './drip-gap-edit';
import { useNowTick } from '@common/hooks/use-now-tick';
import type { AssetKind } from './primitives';
import { useCampaignsRedesignNav } from './nav-context';
import { ActionsMenu } from './action-menu';
import { useDripActions } from './use-drip-actions';
import { deriveDripStatus } from './drip-status';
import {
  resolveBaseSendAt,
  isScheduledSource,
  resolveHeaderTiming,
  deriveDisplayStatus,
  describeSendTiming,
  formatDateTime,
  toValidDate,
  type SendPrecision,
} from './campaign-schedule';
import { deriveAudienceStart, toAudienceIds, type Translate } from './audience-selection';
import { stepEditTargetFor } from './campaign-editability';
import { CampaignContentEditModal } from './campaign-content-edit-modal';
import { CampaignAudienceTab, CampaignWhenTab, type ScheduleRow } from './campaign-setup-tabs';
import {
  campaignSetupLock,
  dripAudiencePatch,
  dripSendAtPatch,
  pendingAudienceRequest,
  pendingSendAtRequest,
  type CampaignDetailTab,
  type SetupSaveOutcome,
} from './campaign-setup-edit';
import { useEditPendingCampaignMutation, useSkipDripStepMutation, useUnskipDripStepMutation } from '@common/reducers/backend-api/backend-api';
import { deriveStepSkipState, isDripStatusSkippable } from './drip-step-skip-state';
import { MessageConfirmationDialog } from '@extension/context/content/components/add-scheduled-message-button/message-confirmation-dialog';

import styles from './sequence-detail.module.scss';

// Class-list helper — the focus marking is the only place here that composes
// classes conditionally, and pulling in a dependency for that would be silly.
const cx = (...parts: Array<string | false | undefined>): string => parts.filter(Boolean).join(' ');

// The centered reading column for this surface — shared by the header band and
// the progression list so both share one left edge (the list surfaces use their
// own, wider `CARD_COLUMN_MAX`: those are grids, this is a single column of
// message cards, which reads badly when stretched).
const DETAIL_COLUMN_MAX = 880;

// How long the "you clicked this one" mark holds before it decays, and how long
// its fade takes (must cover the .45s transition in the stylesheet).
const FOCUS_HOLD_MS = 6000;
const FOCUS_FADE_MS = 500;

// ─── helpers ───

// A drip gap → a date-fns Duration (typed, so `add()` stays type-safe across
// every RecurrenceUnit incl. calendar-aware month/year).
export const gapToDuration = (gap: DripStepGap): Duration => {
  switch (gap.unit) {
    case 'minute': return { minutes: gap.value };
    case 'hour': return { hours: gap.value };
    case 'day': return { days: gap.value };
    case 'week': return { weeks: gap.value };
    case 'month': return { months: gap.value };
    case 'year': return { years: gap.value };
    default: return {};
  }
};

// ─── standalone-campaign bridge ───
// A legacy STANDALONE campaign (no dripId) renders through the SAME detail as a
// 1-step drip. These two pure helpers synthesize the drip-shaped inputs from a
// campaign so every derivation below (status label, per-step logs, summary)
// works unchanged.

// Campaign lifecycle status → the drip status enum the header StatusLabel reads.
export const mapCampaignStatusToDrip = (cs?: CampaignStatus): DripStatus => {
  switch (cs) {
    case 'completed':
    case 'complete-sent':
    case 'complete-delivered':
      return 'completed';
    case 'aborted':
      return 'aborted';
    case 'paused':
      return 'paused';
    case 'active':
    case 'running':
    case 'pending':
    case 'initial':
      return 'active';
    default:
      return 'draft';
  }
};

// Bridge a campaign-model Template into the bt-common step-template shape. The
// two are DIFFERENT nominal types, so we build the object field-by-field:
// `deleted` is required on the step template; `asset` is remapped to the
// TemplateAssetSchema shape (url/name/type); `poll`/`linkPreview` are z.unknown()
// on the step schema and accept the campaign values directly. The template is
// only used to render a preview + media badge, so a field that can't bridge
// cleanly is omitted rather than forced.
//
// Exported so #103's tier-2 content editor can mirror a saved campaign
// template onto its parent drip's `steps[i].template` (display consistency
// only — the campaign patch is the real send-side write) using the EXACT
// SAME field mapping, rather than a second hand-rolled conversion.
export const bridgeTemplate = (t?: CampaignTemplate): StepTemplate => {
  const asset: StepTemplate['asset'] =
    t?.asset && typeof t.asset.url === 'string'
      ? { url: t.asset.url, name: t.asset.name, type: t.asset.type }
      : undefined;
  return {
    deleted: false,
    name: t?.name,
    message: t?.message ?? undefined,
    templateType: t?.templateType,
    snippetShortcut: t?.snippetShortcut,
    sendLinkPreview: t?.sendLinkPreview,
    buttons: t?.buttons,
    poll: t?.poll,
    linkPreview: t?.linkPreview,
    asset,
  };
};

type StepState = 'done' | 'in-flight' | 'scheduled';

interface StepStateMeta {
  /** i18n key for the badge word. A bare `label` string here could never be
   *  translated — the value was baked at module load, long before a `t` exists. */
  labelKey: string;
  /** English fallback, so the badge still reads if a locale is missing the key. */
  label: string;
  cls: 'neutral' | 'brand' | 'success';
  /** Rail-node modifier class. The colour belongs with the rest of the screen's
   *  tokens in the stylesheet, not in an inline `var()` string per state. */
  nodeCls?: string;
}

const STEP_STATE: Record<StepState, StepStateMeta> = {
  done: { labelKey: 'campaignsRedesign.stepBadge.sent', label: 'Sent', cls: 'success', nodeCls: styles.railNodeDone },
  'in-flight': { labelKey: 'campaignsRedesign.stepBadge.sendingNow', label: 'Sending now', cls: 'brand', nodeCls: styles.railNodeFlight },
  scheduled: { labelKey: 'campaignsRedesign.stepBadge.scheduled', label: 'Scheduled', cls: 'neutral' },
};

// Map a step-campaign's status → the timeline node state. A campaign that has
// finished (any `complete*`/`completed`) is 'done'; one mid-dispatch
// ('active'/'running') is 'in-flight'; everything else (no campaign yet,
// 'initial'/'pending'/'paused') is a future/scheduled step.
export const stepStateFromCampaign = (campaign?: Campaign): StepState => {
  const cs = campaign?.campaignStatus;
  if (!cs) return 'scheduled';
  if (cs === 'completed' || cs === 'complete-sent' || cs === 'complete-delivered') return 'done';
  if (cs === 'active' || cs === 'running') return 'in-flight';
  return 'scheduled';
};

// The step badge label — the milestone vocabulary the meter also speaks, derived
// from the SAME progress buckets so the two never disagree: once any recipient has
// been dispatched to WhatsApp (sent/delivered/read/played) → "Sent" (Blueticks did
// its job); no progress / future step → the STEP_STATE default ('Scheduled' for a
// future step, 'Sending now' while in-flight before any message lands). Visuals
// (node colour, rail) stay driven by StepState.
export const stepBadgeLabel = (
  state: StepState,
  progress?: CampaignProgressStats,
): StepStateMeta => {
  if (state === 'scheduled') return STEP_STATE.scheduled;
  if (progress) {
    const dispatched = progress.sending + progress.delivered + progress.read + progress.interacted;
    if (dispatched > 0) return STEP_STATE.done;
  }
  return STEP_STATE[state];
};

// Per-step status figures, all from real data (latest / most-advanced status per
// recipient). `progress` is the SAME CampaignProgressStats the top-bar badge consumes
// (sending / delivered / read / interacted / failed / expired) so the step meter and
// the toolbar badge render identical multi-colour bars off one computeCampaignProgress call.
interface StepStats {
  entered: number;   // recipients still in the sequence when this step launched
  progress: CampaignProgressStats;
}

// Status palette — imported from the single source of truth (campaign-status-colors)
// so this step meter, the delivery-logs filter/badges, and the top-bar badge all share
// one definition. Keyed to the computeCampaignProgress buckets.
const STATUS_COLORS = CAMPAIGN_STATUS_COLORS;

const ZERO_PROGRESS: CampaignProgressStats = {
  sending: 0, pending: 0, delivered: 0, read: 0, interacted: 0, failed: 0, expired: 0,
};

// What a single timeline row needs.
interface DerivedStep {
  stepIndex: number;
  label: string;
  state: StepState;
  template: DripStep['template'];
  campaignId?: string;
  /** The step-campaign's REAL dispatch state. `campaignId` alone says only that
   *  a document exists — which is true from the moment of launch, even for a
   *  campaign due next week — so the edit gates key on this instead (#103). */
  campaignStatus?: CampaignStatus;
  gapAfter?: DripStepGap; // gap that precedes THIS step (taken from prev step)
  /** Wall-clock send time = the base send time (`resolveBaseSendAt`: the step
   *  campaign's / drip's scheduled `dueDate`, else the launch stamp) plus the
   *  cumulative gaps before this step. Undefined when nothing is known. */
  estimatedSendAt?: Date;
  /** Whether `estimatedSendAt` is the STORED due date the dispatcher fires on
   *  ('exact' — step 0 of a scheduled campaign) or a forward walk over the
   *  configured gaps ('estimate'). Drives the card's wording so a projection is
   *  never stated as a fact. */
  sendAtPrecision: SendPrecision;
  hasMedia: boolean;
  mediaKind: AssetKind;
  hasButtons: boolean;
  stats: StepStats;
}

const fmt = (n: number): string => n.toLocaleString();

// ─── StepStatusBreakdown — the PER-MESSAGE delivery numbers, in the step's own row ───
// Each step (Message 1, Follow-up N) shows ITS OWN Sent / Delivered / Read / Failed —
// colour-matched to the bar segments above it — so a sequence reads at a glance without
// hunting an aggregate. This is per-STEP (from that step's `progress`), unlike the
// Summary which is drip-wide funnel (replies / still active / opted out).
// ONE vocabulary for a delivery status, used by every place on this screen that
// names one: the per-step breakdown below the bar, the accessible name of each bar
// SEGMENT, and the status pill in the delivery log. They were three separate sets of
// English literals — the log's was the raw wire value ('interacted') shown to a human.
const STATUS_WORD: Record<string, { labelKey: string; en: string }> = {
  sending: { labelKey: 'campaignsRedesign.status.sent', en: 'Sent' },
  pending: { labelKey: 'campaignsRedesign.status.pending', en: 'Waiting' },
  delivered: { labelKey: 'campaignsRedesign.status.delivered', en: 'Delivered' },
  read: { labelKey: 'campaignsRedesign.status.read', en: 'Read' },
  interacted: { labelKey: 'campaignsRedesign.status.interacted', en: 'Played' },
  played: { labelKey: 'campaignsRedesign.status.interacted', en: 'Played' },
  replied: { labelKey: 'campaignsRedesign.status.replied', en: 'Replied' },
  failed: { labelKey: 'campaignsRedesign.status.failed', en: 'Failed' },
  error: { labelKey: 'campaignsRedesign.status.failed', en: 'Failed' },
  expired: { labelKey: 'campaignsRedesign.status.expired', en: 'Expired' },
};

/** A wire status → the word a human reads for it, in their language. */
const statusWord = (t: TFunction, status: string): string => {
  const word = STATUS_WORD[status];
  return word ? t(word.labelKey, word.en) : status;
};

const STEP_BUCKETS: Array<{ color: string; status: string; get: (p: CampaignProgressStats) => number }> = [
  { color: STATUS_COLORS.sending, status: 'sending', get: (p) => p.sending },
  { color: STATUS_COLORS.delivered, status: 'delivered', get: (p) => p.delivered },
  { color: STATUS_COLORS.read, status: 'read', get: (p) => p.read },
  { color: STATUS_COLORS.failed, status: 'failed', get: (p) => p.failed + p.expired },
];
const StepStatusBreakdown: React.FC<{ progress: CampaignProgressStats }> = ({ progress }) => {
  const { t } = useTranslation(namespace);
  return (
    <div className={styles.breakdown}>
      {STEP_BUCKETS.map((b) => (
        <span key={b.status} className={styles.breakdownItem}>
          <span className={styles.breakdownDot} style={{ background: b.color }} />
          <span className={`num ${styles.breakdownNum}`}>{fmt(b.get(progress))}</span> {statusWord(t, b.status)}
        </span>
      ))}
    </div>
  );
};

// ─── StepMeter — multi-colour delivery bar, identical logic to the top-bar badge ───
// PRIMARY: one progress bar whose SEGMENTS are the wire statuses (sent→delivered→read→
// played, plus failed/expired), each in the shared STATUS_COLORS palette — the exact
// same computeCampaignProgress percents the toolbar MultiColorProgressBadge renders, so
// the two never disagree. The fill spans (totalProcessed / entered): the bar progresses
// in lock-step with the counts. Headline = `reached / entered`, where reached = every
// recipient successfully handed to WhatsApp (processed MINUS failed/expired); failures
// are called out separately on the right and shown as the red tail segment. Below the bar,
// StepStatusBreakdown lists THIS step's Sent/Delivered/Read/Failed (per-message stats).
// SECONDARY row: the drip's "replied & exited" outcome.
const StepMeter: React.FC<{ stats: StepStats }> = ({ stats }) => {
  const { t } = useTranslation(namespace);
  const { entered, progress } = stats;
  const { totalProcessed, totalFailed, percents } = computeCampaignProgress(progress, entered);
  // reached WhatsApp = successfully dispatched (processed minus terminal failures).
  const reached = Math.max(0, totalProcessed - totalFailed);

  // The per-segment breakdown ("120 sent", "80 delivered") used to live ONLY in a
  // native `title`: an OS rectangle no touch or keyboard user could ever open,
  // never translated, and painted outside the extension's shadow root. It is an
  // accessible name now, which is the floor — the breakdown deserves to be VISIBLE
  // under the bar, but that is a layout change and wants a look before it ships.
  // "12 delivered" was English grammar assembled in code. The label is now a
  // translated word beside a number, which every language can order for itself.
  const seg = (pct: number, color: string, status: string, n: number) =>
    pct > 0 ? (
      <div
        className={styles.meterSeg}
        role="img"
        aria-label={t('campaignsRedesign.meter.segment', '{{label}}: {{n}}', { label: statusWord(t, status), n: fmt(n) })}
        style={{ width: `${pct}%`, background: color }}
      />
    ) : null;

  return (
    <div>
      {/* PRIMARY — multi-colour delivery bar */}
      <div>
        <div className={styles.meterHead}>
          <span className={`num ${styles.meterValue}`}>
            {fmt(reached)}<span className={styles.meterTotal}>/{fmt(entered)}</span>
          </span>
          <span className={styles.meterCaption}>{t('campaignsRedesign.meter.reachedWhatsapp', 'reached WhatsApp')}</span>
          <div className={styles.meterSpacer} />
          {totalFailed > 0 && (
            <span className={styles.meterFailed}>
              {t('campaignsRedesign.meter.failedCount', '{{n}} failed', { count: totalFailed, n: fmt(totalFailed) })}
            </span>
          )}
        </div>
        <div className={styles.meterTrack} role="img" aria-label={t('campaignsRedesign.deliveryStatus', 'Delivery status')}>
          {seg(percents.sending, STATUS_COLORS.sending, 'sending', progress.sending)}
          {seg(percents.delivered, STATUS_COLORS.delivered, 'delivered', progress.delivered)}
          {seg(percents.read, STATUS_COLORS.read, 'read', progress.read)}
          {seg(percents.interacted, STATUS_COLORS.interacted, 'interacted', progress.interacted)}
          {seg(percents.failed, STATUS_COLORS.failed, 'failed', progress.failed)}
          {seg(percents.expired, STATUS_COLORS.failed, 'expired', progress.expired)}
        </div>
        {/* PER-MESSAGE stats — this step's own Sent/Delivered/Read/Failed */}
        <StepStatusBreakdown progress={progress} />
      </div>
      {/* The per-step "N replied" row was REMOVED with the reply/exit Summary panel —
          reply tracking isn't implemented, so the count was always 0. */}
    </div>
  );
};

// (ReplyFunnel "Who's still in the sequence" panel removed — reply/exit tracking
//  isn't implemented, so it only ever showed the full audience with 0 exits.)

// ─── delivery logs (real, lazily fetched by campaignId) ───
interface DeliveryLogsProps {
  campaignId?: string;
}
const STATUS_PILL: Record<string, string> = {
  replied: 'wa',
  read: 'wa',
  played: 'wa',
  failed: 'escalate',
  error: 'escalate',
};
const DeliveryLogs: React.FC<DeliveryLogsProps> = ({ campaignId }) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (campaignId) {
      dispatch(DBMessageLogThunks.find({ campaignId, $limit: 50 }));
    }
  }, [dispatch, campaignId]);

  const logs = useAppSelector((s) =>
    campaignId
      ? s.DBMessageLogReducer.entities
      : undefined,
  );
  // messageLogSelectByQuery would re-derive; we keep it light and just read the
  // already-loaded entities for this campaign. If none are loaded yet we render
  // a small "no logs yet" affordance rather than crashing.
  const rows = useMemo(() => {
    if (!campaignId || !logs) return [] as { id: string; phone: string; status: string; body: string; tag?: 'media' | 'poll' }[];
    return Object.values(logs)
      .filter((l): l is NonNullable<typeof l> => !!l && l.campaignId === campaignId)
      .slice(0, 8)
      .map((l) => {
        // BE#647: the ACTUAL body this recipient got (the embedded sent snapshot),
        // so an in-flight edit that made recipients differ is visible per row.
        const m = l.message;
        const text = (m?.message ?? '').trim();
        const tag = m?.isPoll ? ('poll' as const) : (m?.asset ? ('media' as const) : undefined);
        return {
          id: String(l._id ?? l.messageId ?? Math.random()),
          phone: String(l.contactWAId ?? l.participant ?? l.messageId ?? '—'),
          status: String(l.status ?? 'pending'),
          body: text.length > 64 ? `${text.slice(0, 63)}…` : text,
          tag,
        };
      });
  }, [campaignId, logs]);

  // "No delivery logs loaded yet" described OUR fetch, not the user's situation —
  // and read as "we failed to load something". State the fact about the message.
  if (rows.length === 0) {
    return (
      <div className={`fade-in ${styles.logsEmpty}`}>
        {t('campaignsRedesign.noLogsYet', { defaultValue: 'Nothing has been sent from this message yet.' })}
      </div>
    );
  }
  return (
    <div className={`fade-in ${styles.logsList}`}>
      {rows.map((r) => (
        <div key={r.id} className={styles.logsRow} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span className="num">{r.phone}</span>
            <span className={styles.meterSpacer} />
            <span className={`pill ${STATUS_PILL[r.status] ?? 'neutral'}`} style={{ fontSize: 9.5 }}>{statusWord(t, r.status)}</span>
          </div>
          {/* BE#647: what THIS recipient actually received. */}
          {(r.body || r.tag) && (
            <div style={{ fontSize: 10.5, opacity: 0.7, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {r.tag === 'poll' && `📊 ${t('campaignsRedesign.stepPreview.poll', { defaultValue: 'Poll' })}${r.body ? ' · ' : ''}`}
              {r.tag === 'media' && `📎 ${t('campaignsRedesign.stepPreview.media', { defaultValue: 'Media attachment' })}${r.body ? ' · ' : ''}`}
              {r.body}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Scheduled follow-up: a centered "wait", read-only until you edit it ───

// A future ms-duration → a friendly {value, unit} to seed the "send in N unit" input.
export const msToFromNow = (ms: number): { value: number; unit: DripStepGap['unit'] } => {
  const min = Math.max(1, Math.round(ms / 60000));
  if (min < 90) return { value: min, unit: 'minute' };
  const hr = Math.round(min / 60);
  if (hr < 48) return { value: hr, unit: 'hour' };
  const day = Math.round(hr / 24);
  if (day < 14) return { value: day, unit: 'day' };
  return { value: Math.max(1, Math.round(day / 7)), unit: 'week' };
};

// Narrow the persisted DripStepGap (unit reuses the wider campaign
// RecurrenceUnit enum, incl. month/year) to the DelayControl's DelayGap
// (minute/hour/day/week). month/year is never produced by this UI; fall the
// unit back to 'day' for display rather than break the control's type.
const DELAY_UNITS: readonly string[] = ['minute', 'hour', 'day', 'week'];
const toDelayGap = (gap?: DripStepGap): DelayGap | undefined =>
  gap ? { value: gap.value, unit: (DELAY_UNITS.includes(gap.unit) ? gap.unit : 'day') as DelayGap['unit'] } : undefined;

const ScheduledWait: React.FC<{
  step: DerivedStep;
  entered: number;
  anchorAt?: Date; // the previous step's projected send — the gap's anchor
  onEditGap?: (gap: DripStepGap) => void;
  /** The campaign's audience. When this step reaches exactly that many, the
   *  WHO line only repeats the header, so it is dropped. */
  audienceTotal?: number;
}> = ({ step, entered, anchorAt, onEditGap, audienceTotal }) => {
  const { t } = useTranslation(namespace);
  const sendAt = step.estimatedSendAt;
  // Live clock: tick every second when the send is imminent (real countdown),
  // else every 30s. Only this small card re-renders, so it's cheap.
  const soon = !!sendAt && sendAt.getTime() - Date.now() < 90_000;
  const now = useNowTick(soon ? 1000 : 30_000);
  const future = !!sendAt && sendAt.getTime() > now;
  // ALL the wording lives in the pure `describeSendTiming` (unit-tested): a
  // stored due date may be stated, a walked-forward projection is hedged, and a
  // projection whose moment has passed states nothing.
  const timing = describeSendTiming(sendAt, step.sendAtPrecision, now);

  const editable = !!onEditGap && !!anchorAt;
  const [editing, setEditing] = React.useState(false);
  const seed = (): DelayGap => (future && sendAt ? (msToFromNow(sendAt.getTime() - Date.now()) as DelayGap) : { value: 2, unit: 'hour' });
  const [draft, setDraft] = React.useState<DelayGap>(seed);

  // "send in N unit" is always relative to NOW, so the target is always future.
  const previewAt = add(new Date(), gapToDuration({ value: Math.max(1, draft.value), unit: draft.unit }));

  const startEdit = () => { setDraft(seed()); setEditing(true); };
  const save = () => {
    if (onEditGap && anchorAt) {
      // Pure UI: translate "send in X from now" into the existing gap (fine-grained
      // minutes from the previous step's anchor) so the orchestrator still derives
      // the campaign dueDate exactly as before — no mechanics change.
      const gapMin = Math.max(1, Math.round((previewAt.getTime() - anchorAt.getTime()) / 60000));
      onEditGap({ value: gapMin, unit: 'minute' });
    }
    setEditing(false);
  };

  return (
    <div className={styles.wait}>
      {editing ? (
        <div className={styles.waitEdit}>
          {/* Reuse the wizard's DelayControl, framed for the in-flight relative edit.
              The suffix used to be "from now", which put the relativity in the
              sentence twice: "Send in 2 hours from now". `''` rather than
              omitting the prop — DelayControl's `suffix ?? default` would
              otherwise fall through to the WIZARD's "after the previous message
              completes", which is a different (and here wrong) anchor. */}
          <DelayControl
            gap={draft}
            onChange={setDraft}
            leadLabel={t('campaignsRedesign.wait.sendIn', 'Send in')}
            suffix={''}
          />
          {/* The preview was "sends <time>" — a sentence half in JSX, half in code,
              which no translator can re-order. It is one interpolated string now. */}
          <div className={styles.waitAud}>
            <Trans
              t={t}
              i18nKey="campaignsRedesign.wait.sendsAt"
              defaults="sends <0>{{when}}</0>"
              values={{ when: formatDateTime(previewAt) }}
              components={[<strong className={styles.waitStrong} key="0" />]}
            />
          </div>
          <div className={styles.waitActions}>
            <button type="button" className="btn ghost sm" onClick={() => setEditing(false)}>{t('common.cancel', 'Cancel')}</button>
            <button type="button" className="btn primary sm" onClick={save}>{t('common.save', 'Save')}</button>
          </div>
        </div>
      ) : (
        <>
          {/* WHEN, as ONE sentence on ONE line at ONE size: icon, the relative
              time, then the absolute moment. It was three stacked lines at three
              sizes, which made a single fact look like three facts and forced the
              eye down a size ladder to finish reading it. The absolute half is
              tinted rather than shrunk, so the ordering survives without a second
              type size. `subLead` still carries the exact/estimate hedge. */}
          <div className={styles.waitLine}>
            <Hourglass size={14} className={styles.waitIcon} />
            <span>
              {timing.kind === 'relative' && timing.at
                ? <>{timing.headline} {formatDistanceToNow(timing.at, { addSuffix: true })}</>
                : <>{timing.headline}</>}
            </span>
            {timing.at && timing.subLead && (
              <>
                <span className={styles.waitSep}>·</span>
                <span className={styles.waitAt}>{timing.subLead} {formatDateTime(timing.at)}</span>
              </>
            )}
            {editable && (
              <button
                type="button" className={styles.iconBtn} onClick={startEdit}
                aria-label={t('campaignsRedesign.changeSendTime', 'Change when this sends')}
              >
                <Pencil size={12} />
              </button>
            )}
          </div>
          {/* Line two: WHO. "N recipients queued" named our queue, not the
              user's world — and left them to work out whether "queued" meant
              anything had happened yet. Say what will happen. Only when it
              differs from the campaign's audience: otherwise every card repeated
              the header's "6 contacts" verbatim. */}
          {entered !== audienceTotal && (
            <div className={styles.waitAud}>
              <Users size={12} /><strong className={styles.waitStrong}>{fmt(entered)}</strong>{' '}
              {t('campaignsRedesign.willGetThis', { count: entered, defaultValue: 'contacts will get this message' })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Pure content classification for a step card preview: message, else poll
// question, else media, else empty. A poll/media step has NO `.message`, so
// classifying on `.message` alone reads as a bogus "empty" — the regression this
// helper (and its tests) locks down. `text` carries the display string for each
// kind (message/poll → rendered via renderTokens; media/empty → the muted label).
export type StepContentSummary = {
  kind: 'message' | 'poll' | 'media' | 'empty';
  text: string;
};
// `t` is REQUIRED, not optional, for the reason `deriveAudienceLabel` made it so:
// the three labels below are the only thing a reader sees for a step that has no
// message, and each was a bare English literal. An optional `t` would have left
// this call site silently English; a required one makes the compiler name it.
export const stepContentSummary = (
  template: DripStep['template'] | undefined,
  hasMedia: boolean,
  t: Translate,
): StepContentSummary => {
  // Reuse the wizard's ONE template→preview parser (asset/poll/buttons detection)
  // rather than re-deriving it here — keeps the two displays in lock-step.
  const preview = templateToPreview(template);
  const msg = preview.text?.trim();
  if (msg) return { kind: 'message', text: msg };
  if (preview.poll) {
    return { kind: 'poll', text: preview.poll.question?.trim() || t('campaignsRedesign.stepPreview.poll', { defaultValue: 'Poll' }) };
  }
  if (preview.media || hasMedia) {
    return { kind: 'media', text: t('campaignsRedesign.stepPreview.media', { defaultValue: 'Media attachment' }) };
  }
  // The SAME key the wizard and the drip editor use for an unwritten step, so the
  // three empty states cannot say three different things again.
  return { kind: 'empty', text: t('campaignsRedesign.wizard.noMessageSet', { defaultValue: 'Nothing written yet' }) };
};

// A step card's content preview — message, else poll question, else media, else
// empty. Used by BOTH the scheduled and the sent/active cards (a poll/media step
// has no `.message`, so rendering only `.message` shows a bogus "Empty message").
const StepPreview: React.FC<{ step: DerivedStep; sentContent?: SentContentState }> = ({ step, sentContent }) => {
  const { t } = useTranslation(namespace);
  // BE#647: a SENT / in-flight step shows what ACTUALLY went out, not its current
  // template. An in-flight content edit overwrites `step.template`, so rendering it
  // for an already-sent step labels the edited body as "Sent" — the bug. The sent
  // truth (which can differ per recipient when an edit landed mid-send) is derived
  // from the step campaign's delivery logs. A still-scheduled step has no sent
  // content and shows its pending template, which is correct.
  const mode = stepPreviewMode(step.state !== 'scheduled', sentContent);
  if (mode.kind === 'sent-uniform') {
    return <CampaignMessageBubble message={templateToPreviewMessage(mode.message)} align="center" />;
  }
  if (mode.kind === 'sent-mixed') {
    return (
      <span className={styles.previewEmpty}>
        {t('campaignsRedesign.stepPreview.mixedContent', { defaultValue: 'Recipients received different content — see the delivery log below.' })}
      </span>
    );
  }
  if (mode.kind === 'sent-loading') {
    // Sent, but the logs are not loaded yet. Never fall back to the (possibly
    // edited) template for a sent step — that is exactly the mislabel we fix.
    return (
      <span className={styles.previewEmpty}>
        {t('campaignsRedesign.stepPreview.loadingSent', { defaultValue: 'Loading the sent message…' })}
      </span>
    );
  }
  const summary = stepContentSummary(step.template, step.hasMedia, t);
  // A genuinely empty step keeps the muted label — an empty WhatsApp bubble reads
  // as a bug. Everything else (message / poll / media) renders through the SAME
  // MessagePreview bubble the wizard uses, so the two surfaces stay in lock-step.
  if (summary.kind === 'empty') return <span className={styles.previewEmpty}>{summary.text}</span>;
  // Centred on the card's chat ground: this is a preview of one message, not a
  // conversation, so there is no "sent" side for it to hug.
  return <CampaignMessageBubble message={templateToPreviewMessage(step.template)} align="center" />;
};

// Derive "what did this step actually send?" from the step campaign's delivery
// logs. For a sent/in-flight step it ensures the logs are loaded (so the bubble
// can show the real body without the user expanding the log) and classifies them
// as uniform / mixed / none. A scheduled step needs none of this.
function useStepSentContent(step: DerivedStep): SentContentState | undefined {
  const dispatch = useAppDispatch();
  const isSent = step.state !== 'scheduled';
  const campaignId = step.campaignId;
  useEffect(() => {
    if (isSent && campaignId) dispatch(DBMessageLogThunks.find({ campaignId, $limit: 50 }));
  }, [dispatch, isSent, campaignId]);
  const logs = useAppSelector((s) => (isSent && campaignId ? s.DBMessageLogReducer.entities : undefined));
  // `poll` on a template is `z.unknown()` — narrow before reading a question.
  const pollQuestion = ((): string | null => {
    const p = step.template?.poll;
    if (p && typeof p === 'object' && 'question' in p) {
      const q = (p as { question?: unknown }).question;
      return typeof q === 'string' ? q : null;
    }
    return null;
  })();
  return useMemo(() => {
    if (!isSent || !campaignId) return undefined;
    return deriveSentContent(logs, campaignId, {
      usesMergeFields: templateUsesMergeFields(step.template?.message, pollQuestion),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSent, campaignId, logs, step.template?.message, pollQuestion]);
}

// FE#313 — the inter-step WAIT, editable in flight. Renders the wizard's read-only
// DelayControl by default; when `onSave` is present (the following step hasn't sent
// yet) it offers an Edit affordance — DelayControl → Save patches the gap and
// SequenceDetail re-derives the downstream due dates so the change actually fires.
const EditableInterStepWait: React.FC<{ gap: DelayGap; onSave?: (gap: DripStepGap) => void }> = ({ gap, onSave }) => {
  const { t } = useTranslation(namespace);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState<DelayGap>(gap);
  const startEdit = () => { setDraft(gap); setEditing(true); };
  const save = () => {
    // DelayGap.unit (minute/hour/day/week) is a subset of DripStepGap.unit — assignable as-is.
    onSave?.({ value: Math.max(1, draft.value), unit: draft.unit });
    setEditing(false);
  };
  if (editing) {
    return (
      <div className={styles.interWait}>
        <DelayControl gap={draft} onChange={setDraft} />
        <div className={styles.waitActions}>
          <button type="button" className="btn ghost sm" onClick={() => setEditing(false)}>{t('common.cancel', 'Cancel')}</button>
          <button type="button" className="btn primary sm" onClick={save}>{t('common.save', 'Save')}</button>
        </div>
      </div>
    );
  }
  // At rest the wait is a sentence on the rail, not a dashed control: it is
  // read far more often than it is edited, and the wizard's pill made every
  // gap between two cards look like an empty form field.
  return (
    <div className={styles.interWait}>
      <div className={styles.interWaitRow}>
        <Clock size={13} className={styles.interWaitIcon} />
        <span>{t('campaignsRedesign.delayUnit.wait', 'Wait')}</span>
        <strong className={styles.waitStrong}>{gapText(t, gap)}</strong>
        <span className={styles.interWaitTail}>
          {t('campaignsRedesign.delayUnit.afterPrevious', 'after the previous message completes')}
        </span>
        {onSave && (
          <button
            type="button" className={styles.iconBtn} onClick={startEdit}
            aria-label={t('campaignsRedesign.changeWait', 'Change the wait before the next message')}
          >
            <Pencil size={12} />
          </button>
        )}
      </div>
    </div>
  );
};

// ─── StepRow ───
interface StepRowProps {
  step: DerivedStep;
  isLast: boolean;
  /** Prev step's projected send time — the anchor a "send in X from now" edit maps against. */
  anchorAt?: Date;
  /** Present ⇒ the scheduled step's wait is editable in-flight (real drips only). */
  onEditGap?: (stepIndex: number, gap: DripStepGap) => void;
  /** Present ⇒ the scheduled step's content is editable in-flight (opens the wizard editor). */
  onEditContent?: (stepIndex: number) => void;
  /** The wait that FOLLOWS this step (== next step's gapAfter). When set, a
   *  read-only copy of the wizard's DelayControl renders below the card so the
   *  detail shows the inter-step delay exactly like the wizard. Omitted when the
   *  next step is 'scheduled' (its own ScheduledWait already shows that wait). */
  gapAfterThis?: DelayGap;
  /** Present ⇒ the inter-step wait shown below this card (== `gapAfterThis`) is
   *  editable — the following step hasn't sent yet. Patches this step's gapAfter. */
  onEditGapAfter?: (gap: DripStepGap) => void;
  /** The campaign's audience size — lets a scheduled card skip repeating it. */
  audienceTotal?: number;
  /** This is the step the user clicked on the planner calendar. */
  isFocusStep?: boolean;
  /** The focus marking is still being shown. It decays on a timer (see
   *  SequenceDetail) so the answer to "which one did I click" doesn't harden
   *  into a selection the user has to clear. */
  focusVisible?: boolean;
  /** #592c — this step is skipped for the CURRENT cycle (in drip.skippedSteps):
   *  the card shows a "Skipped" badge and offers no skip action. */
  skipped?: boolean;
  /** #592c — present ⇒ a not-yet-sent step of a RECURRING drip can be skipped for
   *  the current cycle (all contacts); opens the confirm naming the send date. */
  onSkipStep?: (stepIndex: number) => void;
  /** #592c — present ⇒ a SKIPPED, not-yet-sent step of a RECURRING drip can be
   *  un-skipped (the skip override is removed); shown in place of the skip action. */
  onUnskipStep?: (stepIndex: number) => void;
}
const StepRow: React.FC<StepRowProps> = ({
  step, isLast, anchorAt, onEditGap, onEditContent, gapAfterThis, onEditGapAfter,
  audienceTotal, isFocusStep = false, focusVisible = false, skipped = false, onSkipStep, onUnskipStep,
}) => {
  const { t } = useTranslation(namespace);
  const st = STEP_STATE[step.state];
  const s = step.stats;
  const [showLogs, setShowLogs] = useState(false);
  // BE#647: for a sent/in-flight step, what actually went out (per recipient).
  const sentContent = useStepSentContent(step);
  const cardRef = React.useRef<HTMLDivElement>(null);
  // A long sequence can put the clicked step well below the fold; landing at the
  // top and leaving the user to find it is the same failure as not marking it.
  React.useEffect(() => {
    if (!isFocusStep) return;
    const el = cardRef.current;
    if (!el) return;
    // Smooth scrolling is driven by animation frames, so in a hidden or
    // throttled tab it silently does NOTHING — measured on the running app:
    // scrollTop stayed at 0 with `smooth` and moved to 179 with the default, on
    // the same element. Putting the step where the user can see it is
    // functional, not decorative, so it must not depend on frames being
    // painted; the same goes for a user who has asked for reduced motion.
    const animate = !document.hidden
      && !window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const bring = () => el.scrollIntoView({ block: 'center', behavior: animate ? 'smooth' : 'auto' });
    bring();
    // …and again once the cards have finished sizing. The message previews,
    // meters and media badges grow after this first render, so the mount-time
    // scroll aims at a layout that no longer exists: measured, it left the last
    // step at the very bottom edge of the viewport (scrollTop 106 of a possible
    // 179). The second pass is a no-op when nothing moved.
    const settle = setTimeout(bring, 300);
    return () => clearTimeout(settle);
  }, [isFocusStep]);
  // Under the redesign shell, "View delivery logs" opens the full, route-driven
  // message-logs view (BulkLogsView, with status filters / waiting-to-send) at
  // /campaigns/:campaignId/logs. Without a nav provider (defensive), fall back to
  // the inline quick-list toggle.
  const redesignNav = useCampaignsRedesignNav();
  return (
    <div className={styles.step}>
      {/* rail */}
      <div className={styles.rail}>
        <div className={cx(styles.railNode, st.nodeCls)}>
          {step.state === 'done' ? <Check size={14} /> : step.state === 'in-flight' ? <Send size={12} /> : step.stepIndex + 1}
        </div>
        {!isLast && <div className={cx(styles.railLine, step.state === 'done' && styles.railLineDone)} />}
      </div>

      {/* card */}
      <div ref={cardRef} className={cx(styles.cardCol, isLast && styles.cardColLast)}>
        {isFocusStep && (
          <div className={cx(styles.focusFlag, !focusVisible && styles.focusFlagGone)}>
            <span className={styles.focusFlagDot} />
            {t('campaignsRedesign.openedFromCalendar', { defaultValue: 'Opened from your calendar' })}
          </div>
        )}
        <div className={cx(styles.card, isFocusStep && styles.cardFocused, isFocusStep && !focusVisible && styles.cardFocusFaded)}>
          <div className={styles.cardHead}>
            <span className={styles.stepName}>
              {step.stepIndex > 0 && <CornerDownRight size={12} />}{step.label}
            </span>
            {/* #592c — a skipped step shows ONLY the "Skipped" badge, not its
                underlying state ("Scheduled"): the skip is the fact that matters
                for this cycle, and two badges read as a contradiction. */}
            {!skipped && (() => {
              const badge = stepBadgeLabel(step.state, step.stats.progress);
              return <span className={`pill ${st.cls}`} style={{ fontSize: 10 }}>{t(badge.labelKey, badge.label)}</span>;
            })()}
            {skipped && (
              <span className="pill neutral" style={{ fontSize: 10 }}>
                {t('campaignsRedesign.stepBadge.skipped', 'Skipped')}
              </span>
            )}
            {step.hasMedia && <AssetBadge kind={step.mediaKind} />}
            {step.hasButtons && <AssetBadge kind="buttons" />}
            {/* The card's one action lives in its header, in the same place on
                every card — it used to float beside the bubble, at a different
                height on each card depending on the message length. */}
            {/* F6 — a SKIPPED step offers no "Edit message": editing a message
                that will not send this cycle is a dead-end action. The card shows
                only the "Skipped" badge + "Undo skip"; editing returns once the
                skip is undone. */}
            {step.state === 'scheduled' && !skipped && onEditContent && (
              <button
                type="button" className={styles.headAction} onClick={() => onEditContent(step.stepIndex)}
                aria-label={t('campaignsRedesign.editThisMessage', 'Edit this message')}
              >
                <Pencil size={12} /> {t('campaignsRedesign.actions.editMessage', 'Edit message')}
              </button>
            )}
            {/* #592b/#592c — skip THIS message for the current cycle (all
                contacts), or UNDO a prior skip. Both are offered only for a
                not-yet-sent step of a recurring drip; the "Skipped" badge above
                stays either way. The pure helper decides which single action
                (skip / undo / none) belongs in this slot. */}
            {(() => {
              const { action } = deriveStepSkipState({
                state: step.state,
                skipped,
                skipEnabled: !!(onSkipStep || onUnskipStep),
              });
              if (action === 'skip' && onSkipStep) {
                return (
                  <button
                    type="button" className={styles.headAction} onClick={() => onSkipStep(step.stepIndex)}
                    aria-label={t('campaignsRedesign.actions.skipThisMessage', 'Skip this message')}
                  >
                    <SkipForward size={12} /> {t('campaignsRedesign.actions.skipThisMessage', 'Skip this message')}
                  </button>
                );
              }
              if (action === 'unskip' && onUnskipStep) {
                return (
                  <button
                    type="button" className={styles.headAction} onClick={() => onUnskipStep(step.stepIndex)}
                    aria-label={t('campaignsRedesign.actions.undoSkip', 'Undo skip')}
                  >
                    <Undo2 size={12} /> {t('campaignsRedesign.actions.undoSkip', 'Undo skip')}
                  </button>
                );
              }
              return null;
            })()}
          </div>

          <div className={styles.cardBody}>
            {step.state === 'scheduled' ? (
              <>
                {/* WHEN this goes out is what you opened the campaign to learn,
                    so it comes FIRST. Below the bubble it sat behind a message
                    body that can run long and carry an image — you had to read
                    past the whole thing to reach the one fact you came for. */}
                <ScheduledWait
                  step={step}
                  entered={s.entered}
                  anchorAt={anchorAt}
                  audienceTotal={audienceTotal}
                  onEditGap={onEditGap ? (gap) => onEditGap(step.stepIndex, gap) : undefined}
                />
                {/* The message on a chat ground, the way the recipient will see
                    it — a lone bubble floating at the card's far edge read as
                    misplaced rather than as "sent". */}
                <div className={styles.chat}><StepPreview step={step} sentContent={sentContent} /></div>
              </>
            ) : (
              <>
                <div className={styles.chat}><StepPreview step={step} sentContent={sentContent} /></div>
                <StepMeter stats={s} />
              </>
            )}
          </div>

          {step.state !== 'scheduled' && (
            <div className={styles.cardFoot}>
              <button
                type="button"
                className={styles.logsBtn}
                onClick={() => (redesignNav && step.campaignId
                  ? redesignNav.openLogs(step.campaignId)
                  : setShowLogs(!showLogs))}
              >
                <ListFilter size={12} /> {!redesignNav && showLogs
                  ? t('campaignsRedesign.hideDeliveryLogs', 'Hide delivery logs')
                  : t('campaignsRedesign.viewDeliveryLogs', 'View delivery logs')}
                {!redesignNav && <ChevronDown size={12} style={{ transform: showLogs ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />}
              </button>
              {!redesignNav && showLogs && <DeliveryLogs campaignId={step.campaignId} />}
            </div>
          )}
        </div>

        {/* Inter-step wait — the same DelayControl the wizard uses. Lives inside
            this step's card column (after the card) so it reads as "then wait N
            before the next message", matching the wizard's layout. Editable in
            flight when `onEditGapAfter` is supplied (the next step hasn't sent). */}
        {gapAfterThis && (
          <EditableInterStepWait gap={gapAfterThis} onSave={onEditGapAfter} />
        )}
      </div>
    </div>
  );
};

// ─── Audience departures — WHO left the sequence, and WHY ───
// When a step reaches fewer recipients than the one before it, it's because
// contacts EXITED the drip (a reply, when stopOnReply is on). This surfaces
// exactly which contact dropped out and when, right on the timeline between the
// step they last received and the one they never will. All from the drip's
// monotonic `exitedRecipients` — no invented data.

// A WhatsApp id → a human label. Names come from the live WA contact cache;
// when a contact was never loaded we fall back to the phone/id itself so the
// row still identifies the departure rather than showing a blank.
const formatWaId = (id: string): string => {
  if (!id) return '';
  if (chatKindOf(id) === 'group') return 'Group chat';
  if (chatKindOf(id) === 'channel') return 'Channel';
  const digits = id.replace(/@.*/, '').replace(/[^0-9]/g, '');
  return digits ? `+${digits}` : id;
};

// Digits-only form of a whatsapp id / stored contact value — the stable key for
// matching an exit id (`972…@c.us`) against an audience row's cell value
// (`972…`, `+972…`, `972…@c.us` all collapse to the same digits).
const waDigits = (v: unknown): string => String(v ?? '').replace(/\D/g, '');

interface DepartureReply {
  text?: string;
  mediaType?: string;
  thumbnail?: string;
}

interface Departure {
  key: string;
  displayId: string; // serialized wa id → fed to ContactAvatar for photo + fallback
  name: string;      // resolved contact name, or the formatted id as a fallback
  sub: string;       // phone/id under the name (empty when name already IS the id)
  reason: string;    // 'replied' | 'opted-out'
  exitedAt?: Date;
  stepIndex: number;
  reply?: DepartureReply; // snapshot of the message that triggered the exit
}

const REASON_META: Record<string, { labelKey: string; label: string; Icon: typeof Reply; pill: string }> = {
  replied: { labelKey: 'campaignsRedesign.exitReason.replied', label: 'Replied', Icon: Reply, pill: 'wa' },
  'opted-out': { labelKey: 'campaignsRedesign.exitReason.optedOut', label: 'Opted out', Icon: X, pill: 'escalate' },
  manual: { labelKey: 'campaignsRedesign.exitReason.removed', label: 'Removed', Icon: UserMinus, pill: 'neutral' },
};
const reasonMeta = (reason: string) => REASON_META[reason] ?? REASON_META.replied;

// The message a recipient replied with, rendered as a compact incoming WA bubble
// under their departure row: thumbnail (image replies) + a media-type chip +
// the text/caption. `dir="auto"` so RTL (Hebrew/Arabic) bodies read correctly.
const MEDIA_META: Record<string, { labelKey: string; label: string; Icon: typeof ImageIcon }> = {
  image: { labelKey: 'campaignsRedesign.replyMedia.photo', label: 'Photo', Icon: ImageIcon },
  sticker: { labelKey: 'campaignsRedesign.replyMedia.sticker', label: 'Sticker', Icon: ImageIcon },
  gif: { labelKey: 'campaignsRedesign.replyMedia.gif', label: 'GIF', Icon: Video },
  video: { labelKey: 'campaignsRedesign.replyMedia.video', label: 'Video', Icon: Video },
  audio: { labelKey: 'campaignsRedesign.replyMedia.audio', label: 'Audio', Icon: Mic },
  ptt: { labelKey: 'campaignsRedesign.replyMedia.voice', label: 'Voice message', Icon: Mic },
  document: { labelKey: 'campaignsRedesign.replyMedia.document', label: 'Document', Icon: FileText },
};
const ReplyPreview: React.FC<{ reply: DepartureReply }> = ({ reply }) => {
  const { t } = useTranslation(namespace);
  const media = reply.mediaType
    ? (MEDIA_META[reply.mediaType] ?? { labelKey: 'campaignsRedesign.replyMedia.attachment', label: 'Attachment', Icon: FileText })
    : undefined;
  return (
    <div className={styles.reply}>
      {reply.thumbnail && (
        <img
          className={styles.replyThumb}
          src={reply.thumbnail}
          alt=""
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
      )}
      <div style={{ minWidth: 0 }}>
        {media && (
          <div className={styles.replyMedia}>
            <media.Icon size={11} /> {t(media.labelKey, media.label)}
          </div>
        )}
        {reply.text
          ? <div dir="auto" className={styles.replyText}>{reply.text}</div>
          : !media && <div className={styles.replyEmpty}>{t('campaignsRedesign.noText', '(no text)')}</div>}
      </div>
    </div>
  );
};

// A branch off the timeline rail: the contacts who exited AT this step. Reads as
// "…and here N contacts stepped out of the sequence", between the message they
// got and the follow-up they won't. Collapses to the first two, expands on click.
const ExitBranch: React.FC<{ departures: Departure[]; isLast: boolean }> = ({ departures, isLast }) => {
  const { t } = useTranslation(namespace);
  const [expanded, setExpanded] = useState(false);
  const n = departures.length;
  const shown = expanded ? departures : departures.slice(0, 2);
  return (
    <div className={`fade-in ${styles.step}`}>
      {/* rail — a distinct branch node + dashed line down to the next step */}
      <div className={styles.rail}>
        <div
          className={styles.exitNode}
          role="img"
          aria-label={t('campaignsRedesign.exitNodeTitle', { defaultValue: 'Contacts stopped receiving this campaign here' })}
        >
          <UserMinus size={11} />
        </div>
        {!isLast && <div className={styles.exitLine} />}
      </div>

      {/* card */}
      <div className={cx(styles.cardCol, isLast && styles.cardColLast)}>
        <div className={styles.exitCard}>
          <div className={styles.exitHead}>
            {/* "left the sequence" was our word for it — this screen calls the
                thing a campaign everywhere else, so a reader had to work out
                that the two were the same object. */}
            <span className={styles.exitTitle}>
              {t('campaignsRedesign.leftCampaign', { count: n, defaultValue: '{{count}} contacts stopped here' })}
            </span>
            <div className={styles.meterSpacer} />
            <span className={styles.exitNote}>
              {t('campaignsRedesign.leftCampaignNote', { defaultValue: 'they will not get the follow-ups' })}
            </span>
          </div>

          <div>
            {shown.map((dep) => {
              const rm = reasonMeta(dep.reason);
              return (
                <div key={dep.key} className={styles.exitRow}>
                  <div className={styles.exitRowLine}>
                    {/* SAME avatar as the contact picker — self-fetches the WhatsApp
                        photo (web + extension) and falls back to the WaPerson/Group/
                        Channel glyph. No bespoke avatar logic here. */}
                    <div className={styles.exitAvatar}>
                      <ContactAvatar contactId={dep.displayId} size={32} textSize={11} />
                    </div>
                    <div className={styles.exitWho}>
                      <div className={styles.exitName}>{dep.name}</div>
                      {dep.sub && <div className={`num ${styles.exitSub}`}>{dep.sub}</div>}
                    </div>
                    <span className={`pill ${rm.pill}`} style={{ fontSize: 9.5, gap: 4 }}>
                      <rm.Icon size={9} /> {t(rm.labelKey, rm.label)}
                    </span>
                    {dep.exitedAt && (
                      <span className={styles.exitWhen}>
                        {formatDistanceToNow(dep.exitedAt, { addSuffix: true })}
                      </span>
                    )}
                  </div>
                  {dep.reply && (dep.reply.text || dep.reply.thumbnail || dep.reply.mediaType) && (
                    <ReplyPreview reply={dep.reply} />
                  )}
                </div>
              );
            })}
          </div>

          {n > 2 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className={styles.exitMore}
            >
              <ChevronDown size={12} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
              {expanded ? 'Show fewer' : `Show all ${fmt(n)}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// (Totals tile removed with the reply/exit Summary panel.)

// ─── media-kind helper from a step template ───
const mediaKindOf = (step: DripStep): { hasMedia: boolean; kind: AssetKind; hasButtons: boolean } => {
  const t = step.template;
  const asset = t?.asset;
  const hasButtons = Array.isArray(t?.buttons) && t.buttons.length > 0;
  if (!asset) return { hasMedia: false, kind: 'image', hasButtons };
  const mime = String(asset.type ?? '');
  const kind: AssetKind = mime.startsWith('video') ? 'video' : 'image';
  return { hasMedia: true, kind, hasButtons };
};

// Per-step stats (sending / confirmed / read / failed) come from the step-
// campaign's latest-status counts; "replied" is the count of recipients who
// exited AT this step with reason 'replied' (the only signal we have that a
// recipient replied — a reply exits the drip when stopOnReply is on). Steps
// without a launched campaign show 0 across the board (no data invented).

// ─── SequenceDetail ───
export interface SequenceDetailProps {
  dripId: string;
  onBack: () => void;
  /** Index of the step the user clicked on the planner calendar (carried through
   *  the route as `?step=N` — see campaigns-redesign-view). Undefined for every
   *  other way into this screen. */
  focusStepIndex?: number;
  /** The tab to open on (`?tab=`); Messages when absent. */
  initialTab?: CampaignDetailTab;
}

export const SequenceDetail: React.FC<SequenceDetailProps> = ({ dripId, onBack, focusStepIndex, initialTab }) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const [editPendingCampaign] = useEditPendingCampaignMutation();

  // The page is where a campaign is both viewed and edited: Messages (the step
  // progression), Audience and When. The two setup tabs replaced the "Edit
  // scheduled campaign" popup. A tab's panel stays mounted once visited, so an
  // unsaved change survives a look at another tab.
  const [tab, setTab] = useState<CampaignDetailTab>(initialTab ?? 'messages');
  const [visitedTabs, setVisitedTabs] = useState<ReadonlySet<CampaignDetailTab>>(() => new Set([initialTab ?? 'messages']));
  const openTab = React.useCallback((next: CampaignDetailTab) => {
    setTab(next);
    setVisitedTabs((prev) => (prev.has(next) ? prev : new Set([...prev, next])));
  }, []);

  const realDrip = useAppSelector((s) => dripsSelectOneObjectByQuery(s.DBDripReducer, { _id: dripId })) as Drip | undefined;
  // A legacy standalone campaign is addressed by its OWN id (no drip exists for it).
  const standaloneCampaign = useAppSelector((s) => campaignsSelectOneObjectByQuery(s.DBCampaignReducer, { _id: dripId })) as Campaign | undefined;

  // fetch the drip + its step-campaigns on mount, plus the campaign whose own id
  // matches this route param (the standalone-campaign path).
  useEffect(() => {
    dispatch(DBDripThunks.find({ _id: dripId }));
    dispatch(DBCampaignThunks.find({ dripId }));
    dispatch(DBCampaignThunks.find({ _id: dripId }));
  }, [dispatch, dripId]);

  const realStepCampaigns = useAppSelector((s) => campaignsSelectByQuery(s.DBCampaignReducer, { dripId })) as Campaign[];

  // When there is no real drip but the id resolves to a standalone campaign,
  // synthesize a 1-step drip + single step-campaign. Everything downstream
  // (derived, campaignByStep keyed on dripStepIndex, audienceStart, StatusLabel,
  // per-step BulkLogsView, summary) then renders with no further changes.
  const isStandalone = !realDrip && !!standaloneCampaign;

  const drip = useMemo<Drip | undefined>(() => {
    if (realDrip) return realDrip;
    if (standaloneCampaign) {
      return {
        _id: standaloneCampaign._id,
        name: standaloneCampaign.name,
        status: mapCampaignStatusToDrip(standaloneCampaign.campaignStatus),
        boardIDArray: standaloneCampaign.boardIDArray,
        stopOnReply: false,
        exitedRecipients: [],
        steps: [{ stepIndex: 0, template: bridgeTemplate(standaloneCampaign.template) }],
      };
    }
    return undefined;
  }, [realDrip, standaloneCampaign]);

  const stepCampaigns = useMemo<Campaign[]>(
    () => (isStandalone && standaloneCampaign
      ? [{ ...standaloneCampaign, dripStepIndex: 0 }]
      : realStepCampaigns),
    [isStandalone, standaloneCampaign, realStepCampaigns],
  );
  // The clicked-step marking is an ANSWER, not a selection: it announces itself,
  // then decays on its own so there is nothing left to dismiss or interpret.
  // `focusVisible` drives the fade; `focusMarked` is whether the mark exists at
  // all. Both start on, and the clock that turns them off is armed further down,
  // once the marked step is actually ON SCREEN — see `focusStepOnScreen`.
  const [focusVisible, setFocusVisible] = useState(focusStepIndex != null);
  const [focusMarked, setFocusMarked] = useState(focusStepIndex != null);

  const taskSumByBoardIdRecord = useAppSelector(getTaskSumByBoardIdRecord);
  const detailNav = useCampaignsRedesignNav();

  // A drip whose EVERY step has a completed campaign is finished — even if the
  // status field lags (e.g. the last step completed while paused, or a race).
  // Reflect that in the header label and hide the now-irrelevant Pause/Resume.
  // Derived by the SHARED selector so the detail + list never disagree.
  const effectiveStatus: DripStatus = useMemo(() => deriveDripStatus(drip, stepCampaigns), [drip, stepCampaigns]);
  const effectiveDrip = useMemo(() => (drip ? { ...drip, status: effectiveStatus } : undefined), [drip, effectiveStatus]);

  // A not-yet-sent step whose content is being edited in-flight (reuses the wizard editor).
  const [editingStepIdx, setEditingStepIdx] = React.useState<number | null>(null);
  // #592c — the step whose skip is being confirmed (single-message skip for the
  // current cycle). The mutation is the SAME backend the calendar chip uses.
  const [skipStepIdx, setSkipStepIdx] = React.useState<number | null>(null);
  const [skipDripStep] = useSkipDripStepMutation();
  // #592c — undo a prior single-message skip (no confirm dialog: undo is the
  // reversible, non-destructive direction, so it fires straight from the card).
  const [unskipDripStep] = useUnskipDripStepMutation();
  // Steps skipped for THIS cycle (drip.skippedSteps) — drives each card's badge.
  const currentCycleSkips = React.useMemo(
    () => new Set<number>((realDrip?.skippedSteps ?? []) as number[]),
    [realDrip?.skippedSteps],
  );
  // The step-card skip is offered only for a recurring drip (the backend action
  // requires a live rruleset); a standalone campaign / non-recurring drip has no
  // "cycle" to skip a message within. F3 — AND only while the drip is in a state
  // the backend will actually honor (active / paused / draft). On a terminal drip
  // (completed / aborted) the BE stashes the skip as a pending override nothing
  // consumes, so offering it there is a promise the product can't keep. Uses the
  // derived EFFECTIVE status so an all-steps-done drip whose stored status lags
  // is treated as completed too.
  const stepSkipEnabled = !!realDrip && !isStandalone
    && !!realDrip.schedule?.isRecurring && !!realDrip.schedule?.rruleset
    && isDripStatusSkippable(effectiveStatus);
  // The current cycle's occurrence instant — what the skip is keyed to (matches
  // the backend's `occurrenceDate == schedule.dueDate` current-cycle test).
  const currentOccurrenceIso = React.useMemo(() => {
    const due = realDrip?.schedule?.dueDate;
    return due ? new Date(due as string | number | Date).toISOString() : undefined;
  }, [realDrip?.schedule?.dueDate]);
  // F2 — AWAIT + unwrap the mutation and read the returned status. A skip/unskip
  // can come back HTTP 200 yet decline to change anything (`too-late` on an undo,
  // `not-recurring` / `not-found`): the optimistic write is already reverted in
  // the mutation's `onQueryStarted` (backend-api) on a non-success status, so all
  // that is left here is to TELL the user, instead of the old fire-and-forget
  // `void` that swallowed the outcome and left a misleading card state.
  const confirmSkipStep = React.useCallback(async () => {
    if (skipStepIdx == null || !currentOccurrenceIso) return;
    const stepIndex = skipStepIdx;
    setSkipStepIdx(null);
    try {
      const res = await skipDripStep({ dripId, occurrenceDate: currentOccurrenceIso, stepIndex }).unwrap();
      if (res?.status !== 'skipped') {
        toast.error(t('campaignsRedesign.actions.skipFailed', "Couldn't skip this message. Please try again."));
      }
    } catch {
      toast.error(t('campaignsRedesign.actions.skipFailed', "Couldn't skip this message. Please try again."));
    }
  }, [skipStepIdx, currentOccurrenceIso, skipDripStep, dripId, t]);
  // #592c — undo a step's skip. Same `{dripId, occurrenceDate, stepIndex}` shape
  // as the skip path. The instant local-store update (clear the "Skipped" badge)
  // is done OPTIMISTICALLY inside the `unskipDripStep` mutation's `onQueryStarted`
  // (backend-api), NOT here — so both this card AND the calendar chip update
  // without a page reload and without any realtime dependency, from one place.
  // F2 — but await the result: undoing the skip of a step the drip has already
  // advanced PAST answers `too-late` (HTTP 200). The optimistic clear is reverted
  // in `onQueryStarted`, so the "Skipped" badge is restored; here we surface WHY.
  const handleUnskipStep = React.useCallback(async (stepIndex: number) => {
    if (!currentOccurrenceIso) return;
    try {
      const res = await unskipDripStep({ dripId, occurrenceDate: currentOccurrenceIso, stepIndex }).unwrap();
      if (res?.status === 'too-late') {
        toast.error(t('campaignsRedesign.actions.unskipTooLate', 'This message has already been sent — the skip can no longer be undone.'));
      } else if (res?.status !== 'unskipped') {
        toast.error(t('campaignsRedesign.actions.unskipFailed', "Couldn't undo the skip. Please try again."));
      }
    } catch {
      toast.error(t('campaignsRedesign.actions.unskipFailed', "Couldn't undo the skip. Please try again."));
    }
  }, [currentOccurrenceIso, unskipDripStep, dripId, t]);
  // A DISPATCHING campaign whose message is being edited through the
  // server-driven editor. Separate state from `editingStepIdx` because the two
  // are different editors over different documents: the step editor writes
  // `drip.steps` directly, this one never writes anything itself.
  //
  // The two states are independent, so open through these helpers, which clear
  // the other one. Nothing routes to both today (`stepEditTargetFor` returns one
  // target and the header menu opens only the campaign editor), but both
  // editors hydrate the STORE-WIDE `DripEditorReducer` singleton — two mounted
  // at once would fight over one working copy.
  //
  // (The audience and the send time are NOT a third editor any more: they are
  // this page's Audience and When tabs, which touch neither singleton.)
  const [editingCampaignId, setEditingCampaignId] = React.useState<string | null>(null);
  const openStepEditor = React.useCallback((stepIndex: number) => {
    setEditingCampaignId(null);
    setEditingStepIdx(stepIndex);
  }, []);
  const openCampaignEditor = React.useCallback((campaignId: string) => {
    setEditingStepIdx(null);
    setEditingCampaignId(campaignId);
  }, []);

  // Actions menu (Pause/Resume, Edit/Rename, Duplicate, Delete) + inline-rename
  // state, all lifted into the shared hook so the detail header and the list
  // cards drive the SAME machinery. `onEdit` opens the wizard (Edit + the
  // Duplicated copy); `onDeleted` returns to the list after a delete.
  const { items, renaming, nameInput, setNameInput, commitRename, cancelRename, savingTemplate, stopSaveTemplate, confirmingDelete, confirmDelete, cancelDelete } =
    useDripActions({
      drip: effectiveDrip,
      realDrip,
      standaloneCampaign,
      dripId,
      isStandalone,
      onEdit: (id) => detailNav?.editCampaign(id),
      // #103: the menu's "Edit message" (offered once a campaign is dispatching)
      // opens the scoped server-driven editor. This detail only opens and closes
      // it — the pause/commit/resume cycle is entirely the modal's three server
      // calls. Also covers a STANDALONE campaign, whose synthesized 1-step
      // sequence renders through this same detail.
      onEditContent: openCampaignEditor,
      // No `onEditSchedule`: the menu's "Edit campaign" opened the scheduled-
      // campaign popup, and on this page the audience and the time are the
      // Audience and When tabs — so the item is not offered here.
      // #103: the menu's TIER-1 "Edit" on a LAUNCHED drip opens the same step
      // editor the step row opens — never the launch wizard, whose completion
      // button re-launches the drip and mints a second campaign over the same
      // audience. Only a DRAFT reaches `onEdit` (see `menuEditTargetFor`).
      onEditStep: openStepEditor,
      onDeleted: onBack,
    });

  // A follow-up's WAIT can be adjusted in flight (it hasn't sent yet) — unlike
  // the audience/message. The wait is stored on the PREVIOUS step's gapAfter;
  // patch through the dbService thunk so the store updates optimistically and
  // the recomputed send-time reflects instantly.
  const handleEditGap = React.useCallback((followUpStepIndex: number, gap: DripStepGap) => {
    if (!realDrip) return;
    const steps = (realDrip.steps ?? []).map((st) => ({ ...st }));
    const prevIdx = steps.findIndex((st) => st.stepIndex === followUpStepIndex - 1);
    if (prevIdx < 0) return;
    steps[prevIdx] = { ...steps[prevIdx], gapAfter: gap };
    dispatch(DBDripThunks.patch({ entity: { ...realDrip, steps } }));
  }, [realDrip, dispatch]);

  // FE#213: 'Stop on reply' is editable post-launch — the flag governs FUTURE
  // follow-ups, which a still-running sequence has ahead of it. Patch through
  // the dbService thunk so the header toggle reflects instantly (optimistic) and
  // persists — the same path as rename / gap edits. Only a REAL drip has a
  // persisted flag; a synthesized standalone campaign stays read-only.
  const editableStopOnReply = canEditStopOnReply(realDrip, isStandalone);
  const handleToggleStopOnReply = React.useCallback((next: boolean) => {
    if (!realDrip) return;
    dispatch(DBDripThunks.patch({ entity: stopOnReplyPatch(realDrip, next) }));
  }, [realDrip, dispatch]);

  // Load each step-campaign's message-log stats — the SAME path the logs view uses
  // (fetchCampaignStatsThunk → fetch real acks, record ONLY advances, re-load the store).
  // Runs immediately on mount, then POLLS so delivered/read acks show up LIVE without the
  // user opening "View delivery logs". To avoid an endless request stream, the poll BACKS
  // OFF: once a few consecutive cycles record nothing new (acks have settled), it stops —
  // fresh changes still arrive via the MQTT broadcast recordDeliveryAck emits, and a
  // remount/revisit restarts it. Cleared on unmount.
  const stepCampaignIdsKey = stepCampaigns.map((c) => c._id).filter(Boolean).join(',');
  useEffect(() => {
    const ids = stepCampaignIdsKey ? stepCampaignIdsKey.split(',') : [];
    if (ids.length === 0) return;
    let idleCycles = 0;
    const IDLE_LIMIT = 3; // ~30s of no new acks → stop polling
    let interval: ReturnType<typeof setInterval> | undefined;
    const fetchAll = async () => {
      const counts = await Promise.all(ids.map((id) => dispatch(fetchCampaignStatsThunk(id)).unwrap().catch(() => 0)));
      const advanced = counts.reduce((a, n) => a + (n ?? 0), 0);
      idleCycles = advanced > 0 ? 0 : idleCycles + 1;
      if (idleCycles >= IDLE_LIMIT && interval) { clearInterval(interval); interval = undefined; }
    };
    fetchAll();
    interval = setInterval(fetchAll, 10000);
    return () => { if (interval) clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, stepCampaignIdsKey]);

  // index step-campaigns by their dripStepIndex
  const campaignByStep = useMemo(() => {
    const m = new Map<number, Campaign>();
    for (const c of stepCampaigns) {
      if (typeof c.dripStepIndex === 'number') m.set(c.dripStepIndex, c);
    }
    return m;
  }, [stepCampaigns]);

  // Hooks must run unconditionally → compute stats for every step-campaign id
  // in a stable order, then read by index. We cap at the drip's step count.
  const steps = useMemo<DripStep[]>(() => drip?.steps ?? [], [drip]);

  // The audience board(s) this sequence targets — stringified + deduped, since
  // `boardIDArray` is `(string|number)[]` and a duplicate id would double-count
  // both the contacts here and the tasks fetched below.
  const audienceBoardIds = useMemo(() => toAudienceIds(drip?.boardIDArray), [drip?.boardIDArray]);
  const audienceBoardKey = useMemo(() => audienceBoardIds.join(','), [audienceBoardIds]);

  // audience start = the audience the campaign ACTUALLY targeted at launch.
  // Prefer the first step-campaign's frozen `contactCount` (stamped at dispatch:
  // who was really sent to, immune to the source board drifting or being deleted
  // afterwards), else the live source board(s) task sum — the ONLY source for a
  // campaign that hasn't dispatched yet. See `deriveAudienceStart`.
  //
  // That task sum comes from `getTaskSumByBoardIdRecord`, which is keyed off the
  // audience BOARDS in the store: it iterates boards and emits no entry for a
  // board it can't see. This route fetches the boards (and their tasks) in the
  // effect below — without that fetch the record is `{}` and a not-yet-dispatched
  // campaign rendered "0 contacts" over a real 40-contact audience.
  //
  // …but only once step 0 has actually DISPATCHED. Before that, a sequence's
  // step campaign carries the count computed when it was CREATED, and the
  // audience can still change on the Audience tab: the backend sync copies the
  // new `boardIDArray` onto the pending campaign but not a new count (dispatch
  // re-stamps it when it freezes the list). So until then the live boards are
  // the only true source — the same rule `campaign-edit-pending` enforces for a
  // standalone campaign by nulling the count on an audience change.
  const step0Dispatched = stepStateFromCampaign(campaignByStep.get(0)) !== 'scheduled';
  const audienceStart = useMemo(
    () => deriveAudienceStart(
      step0Dispatched ? campaignByStep.get(0)?.contactCount : undefined,
      audienceBoardIds,
      taskSumByBoardIdRecord,
    ),
    [step0Dispatched, campaignByStep, audienceBoardIds, taskSumByBoardIdRecord],
  );

  // WHO it goes to, beside "N contacts" in the header: the same one-face-and-
  // "+N" the planner rows draw, with the people on hover/focus. The count is
  // `audienceStart`, so the badge and the words beside it cannot disagree. The
  // faces come from this route's own audience-row fetch below, or the shared
  // batched preview — never a request of their own.
  const headerDestination = useCampaignDestination({ boardIDArray: audienceBoardIds }, audienceStart);
  const headerDestinationHints = useAudienceMemberHints(headerDestination);

  // exits grouped by the step they exited at (from the monotonic exit set). Still used
  // to narrow each step's `entered` count (audience minus prior exits); the reply/exit
  // Summary panel that surfaced these was removed (reply tracking isn't implemented).
  const exits = useMemo<Drip['exitedRecipients']>(() => drip?.exitedRecipients ?? [], [drip]);
  // The WA contact cache — resolves an exited recipient's whatsappId to the name
  // the user actually knows them by. Missing (never-loaded) contacts fall back to
  // the formatted phone/id, so a departure always identifies who left.
  const waContactEntities = useAppSelector((s) => (s as any).DBWaContactsCacheReducer?.entities);

  // Load the AUDIENCE board(s) — needed for TWO things:
  //
  //  • the CONTACT COUNT. `getTaskSumByBoardIdRecord` iterates the audience
  //    boards in the store, so a board that was never fetched yields no entry
  //    and `audienceStart` sums to 0. `type: 'audience'` is part of the query on
  //    purpose: the backend only stamps the denormalised `board.tasksCount` on a
  //    find whose query carries it, and that count is what keeps the header from
  //    flashing 0 in the window before the tasks land.
  //  • the names/avatars the user assigned (the WA contact cache is often empty
  //    in the web dashboard). Each board's `whatsapp-id-cell` column holds the
  //    recipient id; `task.text` the name.
  //
  // The tasks query mirrors the management list's, and `$paginate: false`
  // MATTERS: the tasks service paginates at the app default of 10, so a bare
  // `{ boardId }` find returns the first TEN rows — and the count selector
  // prefers a non-zero loaded count over `tasksCount`, so a 40-contact audience
  // would render "10 contacts". Requesting them all keeps loaded == real.
  useEffect(() => {
    for (const boardId of audienceBoardKey ? audienceBoardKey.split(',') : []) {
      dispatch(DBBoardsThunks.find({ _id: boardId, type: 'audience' }));
      dispatch(DBTasksThunks.find({ boardId, deleted: false, archived: { $ne: true }, $paginate: false }));
      dispatch(DBTaskColumnThunks.find({ boardId, deleted: false }));
    }
  }, [dispatch, audienceBoardKey]);

  const audienceTasks = useAppSelector((s) => {
    const ids = audienceBoardKey ? audienceBoardKey.split(',') : [];
    return ids.flatMap((boardId) => tasksSelectByQuery(s.DBTasksReducer, { boardId }) as any[]);
  });
  const audienceColumns = useAppSelector((s) => {
    const ids = audienceBoardKey ? audienceBoardKey.split(',') : [];
    return ids.flatMap((boardId) => taskColumnSelectByQuery(s.DBTaskColumnReducer, { deleted: false, boardId }) as any[]);
  });

  // whatsappId (digits) → the audience row's name. Built once from the loaded
  // board tasks + their whatsapp-id column, so an exit id resolves to "Matan Eluk"
  // rather than a bare number.
  const audienceNameByDigits = useMemo(() => {
    const waCols = new Set(
      (audienceColumns ?? []).filter((c) => c?.type === 'whatsapp-id-cell').map((c) => String(c._id)),
    );
    const m = new Map<string, string>();
    for (const t of audienceTasks ?? []) {
      if (!t?.customData) continue;
      for (const colId of Object.keys(t.customData)) {
        if (!waCols.has(colId)) continue;
        const val = t.customData[colId]?.value;
        const key = waDigits(val);
        // Map EVERY audience member (name, or the formatted id when unnamed) so
        // the map doubles as the audience-membership set the departure list
        // filters on — a nameless row must still count as "in the sequence".
        if (key) m.set(key, t.text ? String(t.text) : formatWaId(String(val ?? '')));
      }
    }
    return m;
  }, [audienceTasks, audienceColumns]);

  // Departures grouped by the step they exited AT — the deduped, name-resolved
  // view of `exitedRecipients` that the ExitBranch renders. registerReply records
  // MULTIPLE id forms per reply (@c.us + @lid) with one shared `exitedAt`; we
  // collapse those forms back into a single person (group by step + exitedAt),
  // resolve a display name, and bucket by step. So the count here is PEOPLE, not
  // id-forms — matching what actually left, not how many aliases were stamped.
  const departuresByStep = useMemo(() => {
    const groups = new Map<string, { ids: string[]; reason: string; exitedAt?: string; stepIndex: number; reply?: DepartureReply }>();
    for (const e of exits ?? []) {
      const stepIndex = e.stepIndex ?? 0;
      const at = e.exitedAt ? String(e.exitedAt) : '';
      const key = `${stepIndex}|${at}`;
      const g = groups.get(key) ?? { ids: [], reason: e.reason ?? 'replied', exitedAt: at || undefined, stepIndex };
      g.ids.push(e.whatsappId);
      // Both id forms of one reply carry the same snapshot — keep the first present.
      if (!g.reply && (e as any).reply) g.reply = (e as any).reply as DepartureReply;
      groups.set(key, g);
    }
    const byStep = new Map<number, Departure[]>();
    for (const g of groups.values()) {
      // Prefer a phone-form id for name/avatar resolution (@c.us), then @lid.
      const displayId = g.ids.find((i) => i.endsWith('@c.us')) || g.ids.find((i) => i.endsWith('@lid')) || g.ids[0];
      // Name, most-authoritative first: the AUDIENCE row the user set (matched by
      // digits, across id forms) → the WA contact cache (same `calculatedWaContactName`
      // the contact picker uses) → the formatted id. The avatar itself is handled by
      // the shared ContactAvatar off `displayId`, so no pic resolution here.
      const audienceName = g.ids.map((i) => audienceNameByDigits.get(waDigits(i))).find(Boolean);
      // Only surface recipients who were ACTUALLY in this campaign's audience.
      // registerReply records a reply into EVERY active drip (harmless — only a
      // matching @c.us/@g.us actually narrows the send), so a reply in some
      // unrelated group/chat is not a sequence departure and must not pollute the
      // list. Once the audience is known, drop non-members.
      if (audienceNameByDigits.size > 0 && !audienceName) continue;
      const contact =
        getWaContactBySerializedId(waContactEntities, displayId) ||
        g.ids.map((i) => getWaContactBySerializedId(waContactEntities, i)).find(Boolean);
      const resolvedName = audienceName || (contact ? calculatedWaContactName(contact) : undefined);
      const idLabel = formatWaId(displayId);
      const dep: Departure = {
        key: `${g.stepIndex}|${displayId}`,
        displayId,
        name: resolvedName || idLabel,
        sub: resolvedName ? idLabel : '',
        reason: g.reason,
        exitedAt: g.exitedAt ? new Date(g.exitedAt) : undefined,
        stepIndex: g.stepIndex,
        reply: g.reply,
      };
      const arr = byStep.get(g.stepIndex) ?? [];
      arr.push(dep);
      byStep.set(g.stepIndex, arr);
    }
    return byStep;
  }, [exits, waContactEntities, audienceNameByDigits]);

  // People-deduped exit count per step (the ExitBranch shows PEOPLE; the `entered`
  // narrowing below must subtract the same unit, not raw id-forms — otherwise a
  // single @c.us+@lid reply would drop the audience by 2).
  const exitsByStep = useMemo(() => {
    const m = new Map<number, number>();
    for (const [stepIndex, deps] of departuresByStep) m.set(stepIndex, deps.length);
    return m;
  }, [departuresByStep]);

  // Build per-campaign progress-stat lookups — the SAME CampaignProgressStats shape
  // (sending / delivered / read / interacted / failed / expired) the top-bar badge
  // consumes, so the step meter renders an identical multi-colour bar. Both selectors
  // are hook-free memoised, so a single useAppSelector pass over the known campaign
  // ids keeps hook order stable regardless of step count.
  const progressStatsByCampaignId = useAppSelector((s) => {
    const out: Record<string, CampaignProgressStats> = {};
    for (const c of stepCampaigns) {
      if (c._id) out[c._id] = selectCampaignProgressStats(s, c._id);
    }
    return out;
  });

  // The base send time for step 0 — the anchor the later steps' gaps walk
  // forward from. Reads the SCHEDULED due date (step campaign, then drip) before
  // falling back to `startedAt`: `launchDrip` stamps `startedAt` at LAUNCH, so a
  // campaign scheduled for next week used to anchor on "now" (or on nothing at
  // all, for a synthesized standalone campaign) and the whole page lost the one
  // date the user was looking for.
  const sendBase = useMemo(
    () => resolveBaseSendAt({
      campaignDueDate: campaignByStep.get(steps[0]?.stepIndex ?? 0)?.schedule?.dueDate,
      dripDueDate: drip?.schedule?.dueDate,
      startedAt: drip?.startedAt,
    }),
    [campaignByStep, steps, drip?.schedule?.dueDate, drip?.startedAt],
  );

  const derived = useMemo<DerivedStep[]>(() => {
    // cumulative exits BEFORE a given step = sum of exits at all earlier steps.
    let cumulativeExitsBefore = 0;
    // Wall-clock send time, walked forward: step 0 sends at the base, each later
    // step `gapAfter` later. Only step 0 can be 'exact' (the stored due date the
    // dispatcher actually fires on); every walked-forward step is a projection.
    let runningSendAt: Date | undefined = sendBase.at;
    const baseIsExact = isScheduledSource(sendBase.source);
    return steps.map((step, i) => {
      const campaign = campaignByStep.get(step.stepIndex);
      const state = stepStateFromCampaign(campaign);
      const progress = campaign?._id ? (progressStatsByCampaignId[campaign._id] ?? ZERO_PROGRESS) : ZERO_PROGRESS;
      const { hasMedia, kind, hasButtons } = mediaKindOf(step);

      // recipients still in the sequence when this step launched
      const entered = Math.max(0, audienceStart - cumulativeExitsBefore);
      // advance the cumulative counter past THIS step for the next iteration
      cumulativeExitsBefore += exitsByStep.get(step.stepIndex) ?? 0;

      // gap that PRECEDES this step = previous step's gapAfter
      const gapAfter = i > 0 ? steps[i - 1].gapAfter : undefined;

      // this step's estimated send time; then advance the clock by THIS step's
      // own gapAfter for the next iteration.
      const estimatedSendAt = runningSendAt;
      if (runningSendAt && step.gapAfter) {
        runningSendAt = add(runningSendAt, gapToDuration(step.gapAfter));
      }

      return {
        stepIndex: step.stepIndex,
        label: i === 0 ? 'Message 1' : `Follow-up ${i}`,
        state,
        template: step.template,
        campaignId: campaign?._id,
        campaignStatus: campaign?.campaignStatus,
        gapAfter,
        estimatedSendAt,
        sendAtPrecision: (i === 0 && baseIsExact ? 'exact' : 'estimate') as SendPrecision,
        hasMedia,
        mediaKind: kind,
        hasButtons,
        stats: { entered, progress },
      };
    });
  }, [steps, campaignByStep, progressStatsByCampaignId, audienceStart, exitsByStep, sendBase]);

  // Arm the mark's clock only once the marked step is ON SCREEN.
  //
  // This is what made the mark "never appear". The clock used to start when this
  // component MOUNTED — which, on a cold entry, is while the screen still reads
  // "Loading sequence…": the drip, its step-campaigns, its audience boards and
  // their tasks all arrive over the network first. Measured on the running dev
  // app, the step cards did not exist until ~3.5s after navigation, so most of
  // the six seconds was spent behind the spinner and the user arrived to a mark
  // that was already expiring or gone. A slower load, or the extension overlay
  // opening cold, spends all of it. The mark now starts counting from the first
  // frame it can actually be seen in.
  const focusStepOnScreen = focusStepIndex != null && derived.some((d) => d.stepIndex === focusStepIndex);
  useEffect(() => {
    if (!focusStepOnScreen) return;
    setFocusVisible(true);
    setFocusMarked(true);
    const fade = setTimeout(() => setFocusVisible(false), FOCUS_HOLD_MS);
    // Then REMOVE it, rather than trusting the fade to finish the job. A CSS
    // transition only advances while frames are painted, so on a throttled or
    // unpainted tab the collapsed class lands but its opacity/max-height never
    // move — leaving the mark on screen for good, which is the one thing it must
    // never become. Unmounting guarantees the end state everywhere; the
    // transition is only there to make the exit pleasant where it does run.
    const drop = setTimeout(() => setFocusMarked(false), FOCUS_HOLD_MS + FOCUS_FADE_MS);
    return () => { clearTimeout(fade); clearTimeout(drop); };
  }, [focusStepOnScreen, focusStepIndex]);

  // FE#313 — make the inter-step WAIT editable in flight, HONESTLY. Changing a
  // step's `gapAfter` shifts the send time of EVERY later step; a step already
  // materialized into a campaign keeps a FIXED `schedule.dueDate` that nothing
  // re-derives, so we re-stamp it here (the read-only state used to exist ONLY
  // because that re-derive was missing). Only steps that HAVEN'T sent yet
  // ('scheduled') are moved — a past send is history, never re-timed. Both the
  // drip and the moved campaigns patch through the dbService thunks (MQTT +
  // versioning), so the store, the screen, and the dispatcher stay in agreement.
  const handleEditGapAfter = React.useCallback((stepIndex: number, gap: DripStepGap) => {
    if (!realDrip) return;
    const nextSteps = applyGapAfterStep(realDrip.steps, stepIndex, gap);
    dispatch(DBDripThunks.patch({ entity: { ...realDrip, steps: nextSteps } }));
    const projected = projectStepSendDates(nextSteps, sendBase.at);
    for (const d of derived) {
      if (d.stepIndex <= stepIndex) continue;   // only steps AFTER the edited wait shift
      if (d.state !== 'scheduled') continue;     // never re-time an already-sent step
      const campaign = campaignByStep.get(d.stepIndex);
      const newDue = projected.get(d.stepIndex);
      if (!campaign?._id || !newDue) continue;
      dispatch(DBCampaignThunks.patch({
        // dueDate is stored as an ISO string on campaigns (matches the scheduled-edit path).
        entity: { ...campaign, schedule: { ...(campaign.schedule ?? {}), dueDate: newDue.toISOString() } },
      }));
    }
  }, [realDrip, sendBase, derived, campaignByStep, dispatch]);

  // Has this sequence actually begun SENDING? Not "was it launched" — a launched
  // campaign due next week has a campaign document and a `startedAt` while
  // nothing has left the building. Derived from the SAME `stepStateFromCampaign`
  // mapping that colours the timeline nodes, so header and timeline agree.
  const hasDispatched = derived.some((d) => d.state !== 'scheduled');

  // ── Audience / When tabs ──────────────────────────────────────────────────
  // One lock for both: they can change until the first message is sent. The
  // rule and each kind's save path are documented in campaign-setup-edit.ts.
  const setupLock = campaignSetupLock({
    hasDispatched,
    isStandalone,
    dripStatus: realDrip?.status,
    standaloneStatus: standaloneCampaign?.campaignStatus,
  });

  const saveAudience = React.useCallback(async (ids: string[]): Promise<SetupSaveOutcome> => {
    try {
      if (isStandalone && standaloneCampaign?._id) {
        const request = pendingAudienceRequest(standaloneCampaign._id, ids, audienceBoardIds);
        if (!request) return { kind: 'saved' };
        const result = await editPendingCampaign(request).unwrap();
        // The server's write publishes over MQTT, but that is queued while MQTT
        // is down — re-read so the page shows the new audience either way.
        if (result.saved) await dispatch(DBCampaignThunks.find({ _id: standaloneCampaign._id }));
        return result.saved ? { kind: 'saved' } : { kind: 'refused' };
      }
      if (!realDrip) return { kind: 'error' };
      await dispatch(DBDripThunks.patch({ entity: dripAudiencePatch(realDrip, ids) })).unwrap();
      dispatch(DBCampaignThunks.find({ dripId }));
      return { kind: 'saved' };
    } catch {
      return { kind: 'error' };
    }
  }, [isStandalone, standaloneCampaign?._id, audienceBoardIds, editPendingCampaign, realDrip, dispatch, dripId]);

  const saveSendAt = React.useCallback(async (at: Date): Promise<SetupSaveOutcome> => {
    try {
      if (isStandalone && standaloneCampaign?._id) {
        const request = pendingSendAtRequest(standaloneCampaign._id, at, sendBase.at);
        if (!request) return { kind: 'saved' };
        const result = await editPendingCampaign(request).unwrap();
        if (result.saved) await dispatch(DBCampaignThunks.find({ _id: standaloneCampaign._id }));
        return result.saved ? { kind: 'saved' } : { kind: 'refused' };
      }
      if (!realDrip) return { kind: 'error' };
      await dispatch(DBDripThunks.patch({ entity: dripSendAtPatch(realDrip, at) })).unwrap();
      // Step 0's campaign is moved by the backend sync (atomic, pending-only).
      // A LATER step that already has a campaign keeps a fixed dueDate nothing
      // re-derives — re-stamp any still-scheduled one, as the wait edit does.
      const projected = projectStepSendDates(realDrip.steps, at);
      for (const d of derived) {
        if (d.stepIndex === (steps[0]?.stepIndex ?? 0) || d.state !== 'scheduled') continue;
        const campaign = campaignByStep.get(d.stepIndex);
        const newDue = projected.get(d.stepIndex);
        if (!campaign?._id || !newDue) continue;
        dispatch(DBCampaignThunks.patch({
          entity: { ...campaign, schedule: { ...(campaign.schedule ?? {}), dueDate: newDue.toISOString() } },
        }));
      }
      dispatch(DBCampaignThunks.find({ dripId }));
      return { kind: 'saved' };
    } catch {
      return { kind: 'error' };
    }
  }, [isStandalone, standaloneCampaign?._id, sendBase.at, editPendingCampaign, realDrip, dispatch, derived, steps, campaignByStep, dripId]);

  // The When tab's schedule: every message's send time walked forward from a
  // first-send time — the picked one while editing, so the list moves with it.
  const scheduleRowsFor = React.useCallback((firstSendAt: Date | undefined): ScheduleRow[] => {
    const projected = projectStepSendDates(steps, firstSendAt);
    return derived.map((d, i) => ({
      key: String(d.stepIndex),
      label: i === 0
        ? t('campaignsRedesign.wizard.messageLabel', 'Message 1')
        : t('campaignsRedesign.wizard.followupLabel', 'Follow-up {{number}}', { number: i }),
      gapLabel: i > 0 && d.gapAfter
        ? t('campaignsRedesign.setup.gapAfterPrevious', { defaultValue: '{{gap}} after the previous message', gap: gapText(t, toDelayGap(d.gapAfter)) })
        : undefined,
      at: projected.get(d.stepIndex),
      state: d.state,
    }));
  }, [steps, derived, t]);

  if (!drip) {
    return (
      <div className={`col ${styles.root} ${styles.loading}`} style={{ ['--seq-column' as any]: `${DETAIL_COLUMN_MAX}px` }}>
        <div className={styles.column}>
          <div className={styles.backRow}>
            <button type="button" onClick={onBack} className={styles.backBtn}>
              <ChevronLeft size={15} className={styles.backIcon} /> {t('common.back', 'Back')}
            </button>
          </div>
          <div className={styles.loadingText}>{t('campaignsRedesign.loading', { defaultValue: 'Loading campaign…' })}</div>
        </div>
      </div>
    );
  }

  // The header's timing slot. `Started —` (the old text for anything not yet
  // sending) answered nothing — it is exactly what the customer reported not
  // being able to find. Until the first message goes out, state the SCHEDULE.
  //
  // The `none` branch is reached only when the drip has NEITHER a startedAt nor a
  // scheduled base — nothing on the document says when this ran or will run. It
  // read "Started —": a verb in the past tense attached to an em dash, which
  // asserts the campaign started and then refuses to say when. It now says the
  // thing that is actually true, which is the Hebrew this key has carried all
  // along ("מועד ההתחלה לא ידוע") — the two locales agreed on the meaning and
  // only the English was hedging.
  //
  // Formatted with the LOCALE's own date+time convention (see formatDateTime) —
  // the convention already used by the scheduled-step card below — rather than the
  // old `toLocaleDateString('en-GB')`, which is DATE-ONLY: it would drop the
  // "17:43" that is half the answer.
  const headerTiming = resolveHeaderTiming({ base: sendBase, startedAt: drip.startedAt, hasDispatched });
  const timingText =
    headerTiming.kind === 'started'
      ? t('campaignsRedesign.startedAt', { defaultValue: 'Started {{when}}', when: formatDateTime(headerTiming.at) })
      : headerTiming.kind === 'scheduled'
        ? t('campaignsRedesign.scheduledFor', { defaultValue: 'Scheduled for {{when}}', when: formatDateTime(headerTiming.at) })
        : t('campaignsRedesign.startedUnknown', { defaultValue: 'Start time unknown' });

  // (The sending-window line — "Sends any time of day" under a clock — is gone:
  // the owner called it redundant, and the per-campaign send window it reported
  // is not implemented (bt-common campaign.ts marks those fields NOT
  // IMPLEMENTED), so it was a statement about a setting nobody can set.)

  return (
    <div className={`col ${styles.root}`} style={{ ['--seq-column' as any]: `${DETAIL_COLUMN_MAX}px` }}>
      {/* header. The surface + bottom rule span the full width (so the header
          reads as a band), but its CONTENT sits in the same centered
          `DETAIL_COLUMN_MAX` column as the progression below — on a wide
          screen the old full-bleed header left the title and the step cards
          stranded against the left edge, kilometres from the actions menu. */}
      <div className={styles.headerBand}>
       <div className={styles.headerInner}>
        <div className={styles.backRow}>
          {/* "Back", not "Campaigns": this screen is reached from the campaigns
              list AND from the planner calendar, and `onBack` returns to
              whichever it was — naming one destination was wrong half the time. */}
          <button type="button" onClick={onBack} className={styles.backBtn}>
            <ChevronLeft size={15} className={styles.backIcon} /> {t('common.back', 'Back')}
          </button>
        </div>
        {/* The header identifies the campaign and offers its campaign-level
            controls. That is ALL it is for now. It had grown into three stacked
            bands with no shared rhythm — an icon tile + title + pill, then a
            three-column labelled facts band, then a toggle adrift on a line of
            its own — and its WHEN restated, in a second format, exactly what the
            step block below now says first and more precisely. So: WHEN is gone
            from here (stated once, where it belongs); the megaphone tile is gone
            (every campaign wore the same one, so it identified nothing and only
            pushed the title off the screen's single inline edge); the two facts
            that DO identify a campaign and are stated nowhere else — who it goes
            to and the hours it may send in — became one quiet meta line under
            the title; and the Stop-on-reply toggle joined the actions menu,
            because a control belongs with controls, not among facts. */}
        <div className={styles.titleRow}>
          <div className={styles.identity}>
            <div className={styles.titleLine}>
              {renaming ? (
                <input
                  autoFocus
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename();
                    else if (e.key === 'Escape') cancelRename();
                  }}
                  placeholder={t('campaignsRedesign.campaignNamePlaceholder', 'Campaign name')}
                  className={styles.nameInput}
                />
              ) : (
                <h1 className={styles.title}>{drip.name ?? t('campaignsRedesign.untitledCampaign', { defaultValue: 'Untitled campaign' })}</h1>
              )}
              {/* DISPLAY-ONLY narrowing: a drip stores `active` from the moment
                  it is launched, so a campaign due next week wore a green
                  "Running" badge while nothing had been sent (and while its own
                  list card said "Scheduled"). `deriveDripStatus` itself is
                  untouched — it also gates Pause/Resume and drives the list
                  card, so the fix lives here, at the badge. */}
              {effectiveStatus && <StatusLabel status={deriveDisplayStatus(effectiveStatus, hasDispatched)} />}
              {/* FE#213: read-only pill ONLY when the toggle isn't editable
                  (a synthesized standalone campaign). Editable sequences get the
                  live toggle in the control cluster instead — no duplicate. */}
              {!editableStopOnReply && drip.stopOnReply && (
                <span className="pill wa" style={{ fontSize: 10 }}>
                  <Reply size={10} /> {t('campaignsRedesign.card.stopsOnReply', 'stops on reply')}
                </span>
              )}
            </div>
            {/* Faces + value, no labels: "42 contacts" beside the people it
                means says what it is. The group carries its own accessible
                name ("42 recipients, including …"). */}
            <div className={styles.meta}>
              <span className={styles.metaItem}>
                {/* The word is pluralised and translated; the FIGURE stays in its
                    own `.num` span, which is a monospace treatment — pulling the
                    word inside it would set "contacts" in mono too. */}
                {/* The faces say WHO; the words beside them say how many. The
                    generic people icon this replaced said neither. */}
                <DestinationWithList plan={headerDestination} size="inline" hints={headerDestinationHints} />
                <span className={`num ${styles.metaValue}`}>{fmt(audienceStart)}</span>{' '}
                {t('campaignsRedesign.contactsWord', { count: audienceStart, defaultValue: 'contacts' })}
              </span>
              {/* The header states WHEN only where it is the ONLY statement of
                  it: once a sequence has dispatched, its sent steps show a
                  delivery meter, not a send time, so "Started …" lives nowhere
                  else. Before dispatch the step block says it first and more
                  precisely, and repeating it here in a second format is the
                  duplication that made this header feel like noise. */}
              {headerTiming.kind === 'started' && (
                <>
                  <span className={styles.metaSep} aria-hidden="true">·</span>
                  <span className={styles.metaItem}><span className={styles.metaValue}>{timingText}</span></span>
                </>
              )}
            </div>
          </div>
          {/* Controls, together: the one live setting and the actions menu. */}
          <div className={styles.controls}>
            {/* FE#213: live 'Stop on reply' toggle — editable after launch, not
                only in the pre-launch wizard. Patches the drip via the dbService
                thunk (optimistic + persisted), so this reflects instantly. */}
            {editableStopOnReply && (
              <span className={styles.setting}>
                <Reply size={13} /> {t('campaignsRedesign.wizard.stopOnReplyTitleShort', 'Stop on reply')}
                <Toggle value={currentStopOnReply(drip)} onChange={handleToggleStopOnReply} size="sm" />
              </span>
            )}
            <ActionsMenu items={items} />
          </div>
          <SaveAsDripTemplateModal open={savingTemplate} drip={realDrip} onClose={stopSaveTemplate} />
          {confirmingDelete && (
            <MessageConfirmationDialog
              // Same words as the campaigns list's delete dialog, from the same
              // keys — the confirmation for one action should not be two texts.
              title={t('campaignsRedesign.deleteDialog.title', 'Delete this campaign and stop sending?')}
              description={t('campaignsRedesign.deleteDialog.body', 'Anyone who has not received it yet never will. Messages already sent cannot be recalled.')}
              confirmLabel={t('campaignsRedesign.deleteDialog.confirm', 'Stop and delete')}
              destructive
              onClose={(value?: string) => { if (value !== undefined) confirmDelete(); else cancelDelete(); }}
            />
          )}
          {/* #592c — confirm a single-message skip, NAMING the send date (the
              current cycle's occurrence). Non-destructive: the rest of the cycle
              and the series keep sending. */}
          {skipStepIdx != null && (
            <MessageConfirmationDialog
              title={t('campaignsRedesign.actions.skipThisMessageTitle', 'Skip this message?')}
              description={
                currentOccurrenceIso
                  ? t('campaignsRedesign.skipStepDialog.body', {
                      defaultValue: 'This message will be skipped for everyone on the {{when}} send. The rest of the sequence keeps sending, and the next cycle sends it as usual.',
                      when: formatDateTime(new Date(currentOccurrenceIso)),
                    })
                  : t('campaignsRedesign.actions.skipThisMessagePrompt', 'This message will be skipped for everyone on this send. The rest of the sequence keeps sending.')
              }
              confirmLabel={t('campaignsRedesign.actions.skipThisMessageConfirm', 'Skip this message')}
              onClose={(value?: string) => { if (value !== undefined) confirmSkipStep(); else setSkipStepIdx(null); }}
            />
          )}
        </div>
        {/* The page's three tabs. Each names its current value, so the header
            answers "to whom" and "when" without opening anything; the lock
            says, before the click, that a tab is read-only now. */}
        <div className={styles.tabs} role="tablist">
          {([
            {
              key: 'messages' as const,
              icon: MessageSquare,
              label: t('campaignsRedesign.setup.tabMessages', 'Messages'),
              hint: fmt(derived.length),
              locked: false,
            },
            {
              key: 'audience' as const,
              icon: Users,
              label: t('campaignsRedesign.wizard.stepAudience', 'Audience'),
              // The header's own wording: the figure, then the pluralised word.
              hint: `${fmt(audienceStart)} ${t('campaignsRedesign.contactsWord', { count: audienceStart, defaultValue: 'contacts' })}`,
              locked: Boolean(setupLock),
            },
            {
              key: 'when' as const,
              icon: CalendarClock,
              label: t('campaignsRedesign.scheduledEdit.tabWhen', 'When'),
              // The value the When panel leads with: the real start once
              // sending began, the scheduled time before.
              hint: (() => {
                const at = (hasDispatched ? toValidDate(drip.startedAt as any) : undefined) ?? sendBase.at;
                return at ? formatDateTime(at) : undefined;
              })(),
              locked: Boolean(setupLock),
            },
          ]).map(({ key, icon: Icon, label, hint, locked }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`${styles.tab} ${tab === key ? styles.tabActive : ''}`}
              onClick={() => openTab(key)}
            >
              <Icon size={14} />
              <span>{label}</span>
              {hint && <span className={styles.tabHint}>{hint}</span>}
              {locked && <Lock size={12} className={styles.tabLock} aria-label={t('campaignsRedesign.setup.lockedAria', 'Locked')} />}
            </button>
          ))}
        </div>
       </div>
      </div>

      <div className={`scroll ${styles.scrollBody}`}>
        {visitedTabs.has('audience') && (
          <div className={styles.column} hidden={tab !== 'audience'}>
            <CampaignAudienceTab storedIds={audienceBoardIds} lock={setupLock} onSave={saveAudience} />
          </div>
        )}
        {visitedTabs.has('when') && (
          <div className={styles.column} hidden={tab !== 'when'}>
            <CampaignWhenTab
              storedAt={sendBase.at}
              lock={setupLock}
              startedAt={hasDispatched ? toValidDate(drip.startedAt as any) : undefined}
              rruleset={(() => {
                const schedule = isStandalone ? standaloneCampaign?.schedule : drip.schedule;
                return schedule?.isRecurring ? (schedule.rruleset as string | undefined) : undefined;
              })()}
              multi={derived.length > 1}
              rowsFor={scheduleRowsFor}
              onSave={saveSendAt}
              onOpenMessages={() => openTab('messages')}
            />
          </div>
        )}
        <div hidden={tab !== 'messages'}>
        {/* Single centered column, sharing `DETAIL_COLUMN_MAX` with the header
            above so the back button, the title and the step cards all share one
            left edge. The reply/exit "Summary" side panel (Who's still in the
            sequence / Total replies / Still active / Opted out) was REMOVED — reply
            tracking isn't implemented, so those figures were always 0 and misleading.
            Re-add a summary here once per-message reply capture lands. */}
        <div className={styles.column}>
          <Eyebrow style={{ marginBottom: 12 }}>{t('campaignsRedesign.messagesInCampaign', { defaultValue: 'Messages in this campaign' })}</Eyebrow>
          {derived.map((d, i) => {
            // The configured wait after THIS step == the next step's gapAfter.
            // Always show it (like the wizard) so drafts/scheduled sequences
            // also display the delay — the next step's ScheduledWait only shows
            // runtime send-timing ("sends when ready" for a draft), never the
            // configured gap, so this is the sole place a draft's delay appears.
            const next = derived[i + 1];
            const gapAfterThis = next ? toDelayGap(next.gapAfter) : undefined;
            // FE#313 — the wait AFTER this step is editable while the NEXT step is
            // still 'scheduled' (hasn't sent). `handleEditGapAfter` patches this
            // step's gapAfter AND re-derives the downstream campaigns' due dates,
            // so the change actually fires (no longer the read-only "honest state").
            const canEditGapAfter = !!realDrip && !!next && next.state === 'scheduled';
            // Contacts who exited AFTER receiving THIS step (a reply, when
            // stopOnReply is on) — rendered as a branch between this step and the
            // follow-up they'll never get. This is exactly WHY the next step's
            // "reached" count is smaller than this one's.
            const departures = departuresByStep.get(d.stepIndex);
            const isLastStep = i === derived.length - 1;
            // #103: gate the CONTENT edit on REAL dispatch state, not on
            // whether a campaign DOCUMENT exists. `launchDrip` creates step 0's
            // campaign AT LAUNCH with a future dueDate, so the old
            // `!d.campaignId` test locked a campaign scheduled for next week
            // even though nothing had been sent. `stepEditTargetFor` also picks
            // WHICH editor applies — see its own doc comment for the tier split.
            const editTarget = stepEditTargetFor(d.campaignId, d.campaignStatus);
            return (
              <React.Fragment key={i}>
                <StepRow
                  step={d}
                  isFocusStep={focusMarked && focusStepIndex != null && d.stepIndex === focusStepIndex}
                  focusVisible={focusVisible}
                  isLast={isLastStep && !(departures && departures.length > 0)}
                  anchorAt={derived[i - 1]?.estimatedSendAt}
                  audienceTotal={audienceStart}
                  gapAfterThis={gapAfterThis}
                  onEditGapAfter={canEditGapAfter ? (gap) => handleEditGapAfter(d.stepIndex, gap) : undefined}
                  // The WAIT stays gated on campaign EXISTENCE — deliberately
                  // NOT on `editTarget`. `createStepCampaign` fixes
                  // `schedule.dueDate` when the campaign document is created,
                  // and nothing downstream re-derives it: the tier-1 sync hook
                  // (`syncPendingStepCampaign`) copies `template` +
                  // `boardIDArray` only. So once the campaign exists, editing
                  // the wait moves the estimate on screen while the campaign
                  // still fires at the old time — an edit that looks like it
                  // took and did not. Read-only is the honest state until the
                  // sync learns to re-derive `dueDate`.
                  onEditGap={realDrip && !d.campaignId ? handleEditGap : undefined}
                  onEditContent={
                    !realDrip || editTarget === 'locked'
                      ? undefined
                      : editTarget === 'step-editor'
                        ? openStepEditor
                        // `d.campaignId` is what made the target 'campaign-editor'.
                        : () => openCampaignEditor(d.campaignId as string)
                  }
                  // #592c — single-message skip / undo-skip on the step card
                  // (recurring drips). The card's pure helper picks which of the
                  // two (or neither) to show per step state.
                  skipped={currentCycleSkips.has(d.stepIndex)}
                  onSkipStep={stepSkipEnabled ? setSkipStepIdx : undefined}
                  onUnskipStep={stepSkipEnabled ? handleUnskipStep : undefined}
                />
                {departures && departures.length > 0 && (
                  <ExitBranch departures={departures} isLast={isLastStep} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        </div>
      </div>

      {editingStepIdx != null && realDrip && (
        <DripStepEditModal
          drip={realDrip}
          stepIndex={editingStepIdx}
          onClose={() => setEditingStepIdx(null)}
        />
      )}

      {/* #103. Two things here are load-bearing, not style:
          - `key={editingCampaignId}` — the modal latches its `begin` call in a
            ref that is NOT keyed on the campaign, so swapping the prop on a
            mounted instance would skip `begin` for the new campaign and leave
            the previous one's session state on screen. The key forces a fresh
            instance per campaign.
          - the modal is CONDITIONALLY RENDERED, never hidden behind an `open`
            flag. Its unmount cleanup is what sends the abort that resumes the
            campaign; keeping it mounted would strand the pause. (It refuses its
            own close while a call is in flight, so this can't unmount mid-save.) */}
      {editingCampaignId && (
        <CampaignContentEditModal
          key={editingCampaignId}
          campaignId={editingCampaignId}
          onClose={() => setEditingCampaignId(null)}
        />
      )}
    </div>
  );
};
